import * as THREE from 'three';
import { GameLoop, type GameLoopStepClock } from './GameLoop';
import {
  FIXED_UPDATE_HZ,
  FIXED_UPDATE_DT,
  MAX_GAMEPLAY_CATCHUP_STEPS,
  SLOW_FRAME_THRESHOLD_STEPS,
  THERMAL_RENDER_FPS_CAP,
  clampVisualSmoothingDt,
  parseRenderFpsCap,
} from './FrameTiming';
import { ServiceLocator } from '../engine/ServiceLocator';
import { InputSystem } from '../engine/systems/InputSystem';
import { AudioSystem } from '../engine/systems/AudioSystem';
import { AssetSystem } from '../engine/systems/AssetSystem';
import { RenderSystem } from '../engine/systems/RenderSystem';
import { TrackSystem } from '../engine/systems/TrackSystem';
import { DiagnosticsSystem } from '../engine/systems/DiagnosticsSystem';
import { PostFXSystem } from '../engine/systems/PostFXSystem';
import { DrsSystem, type RenderScaleMode as DrsRenderScaleMode } from '../engine/systems/DrsSystem';
import { UISystem } from '../engine/systems/UISystem';
import { RaceSystem } from '../engine/systems/RaceSystem';
import { driftAudioSignal, step as physicsStep, speed as racerSpeed } from './Physics';
import { activePhysics } from './physics-versions';
import {
  loadCachedPodRacerRigTuning,
  POD_RACER_ASSEMBLY_ASSETS,
} from './PodRacerConfig';
import {
  isAssistedMode,
  type RaceMode,
} from './DrivingAssists';
import {
  applyWallContactResponse,
  makeContactState,
  resolveMeshCollision,
  resolveObstacleCollision,
  resolveSoftScrapeCollision,
  resolveTerrainSlopeCollision,
  resolveTrackCorridorCollision,
  updateContactState,
  type ContactState,
  type MeshCollider,
  type MovementSegment2D,
  type ObstacleObb,
  type SoftBarrierHit,
  type WallHit,
} from './Collision';
import { Racer, type ScrapeEmitter } from './Racer';
import { FollowCamera } from './Camera';
import { applyCachedCameraTuning, applyDefaultCameraTuning } from './CameraConfig';
import {
  loadTrack,
  getStartPose,
  isOnTrack,
  locateTrackPosition,
} from '../track/TrackLoader';
import {
  AGGRESSIVE_AUTOPILOT_PARAMS,
  TIMID_AUTOPILOT_PARAMS,
  completeAutopilotLap,
  computeAutopilotInput,
  createAutopilotState,
  getAutopilotLaps,
  resetAutopilotRun,
  startAutopilotLap,
} from './Autopilot';
import {
  AGGRESSIVE_OPPONENT_PROFILE,
  TIMID_OPPONENT_PROFILE,
  createOpponentControllerState,
  placeOpponentAtStart,
  resetOpponentController,
} from './OpponentController';
import {
  countdownLabel,
  gridPoseAtLateral,
  pinRacerStateForStartHold,
  rolledBackProgress,
  startHoldThrottleSignal,
  type RaceStartPose,
} from './RaceStart';
import { sampleTrackAIPath } from './TrackAIPath';
import { resolveTrackSelection } from '../track/TrackRegistry';
import { trackSurfaceHeightAtPosition } from '../track/TrackElevation';
import { buildTrackMeshes } from '../track/TrackRenderer';
import { WrongWayDetector } from './WrongWayDetector';
import { OffTrackBudget } from './OffTrackBudget';
import { LapTimer } from '../timing/LapTimer';
import {
  GhostRecorder,
  buildGhostProgressSamples,
  buildGhostSectorTimes,
  computeGhostProgressDelta,
  loadGhost,
  saveGhost,
  shouldSaveGhost,
  type GhostProgressSample,
  type GhostRecording,
  type GhostStartState,
} from '../timing/Ghost';
import { GhostShip } from './GhostShip';
import { PHYSICS_VERSION } from './physics-versions';
import { getLeaderboardGhostCandidates, ghostMatchesLap, isOwnLeaderboardRow } from '../timing/GhostTarget';
import { buildTrainingGhost } from '../timing/TrainingGhost';
import { AdaptiveTrainingRabbit } from '../timing/AdaptiveTrainingRabbit';
import { HUD, type RaceTargetState } from '../timing/HUD';
import { projectGhostMarkerToScreen } from '../timing/GhostMarker';
import { Minimap } from '../timing/Minimap';
import { createRenderer, isAndroidDevice, isMobileDevice, isWebGLRenderer } from '../render/Renderer';
import { createStaticBundle } from '../render/StaticBundles';
import { createAdaptiveQualityGate } from '../render/AdaptiveQuality';
import { ContactShadow } from '../render/ContactShadow';
import { PixelationPipeline } from '../render/Pixelation';
import { SfxPipeline, type SfxOptions, type SfxRuntime } from '../render/Sfx';
import { motionBlurPrefs } from '../render/MotionBlurPrefs';
import { DustTrail, EngineTrail, GroundStreaks, ScrapeMarks } from '../render/Particles';
import { buildTerrain } from '../render/Terrain';
import { buildScenery } from '../render/Scenery';
import { bootSceneStyleRig } from '../render/SceneStyleRig';
import { applyCloudUniforms, buildCloudField } from '../render/Clouds';
import { createVolumetricCloudDome, type VolumetricCloudDome } from '../render/VolumetricClouds';
import { buildTrackFurniture } from '../render/TrackFurniture';
import {
  listenToStyleConfigChange,
  loadStyleForTrack,
  type StyleConfig,
} from '../render/StyleConfig';
import { resolveStyle, type ResolvedStyle } from '../render/OverrideResolver';
import {
  applyEnvironmentStyle,
  applyLiveSkyStyle,
  applyRendererStyle,
  applySkySceneStyle,
  applySunRigStyle,
  directionFromAnglesToTarget,
  fillLightDirectionToTarget,
  keyLightDirectionToTarget,
  rimLightDirectionToTarget,
  updateSunRig,
} from '../render/SceneLighting';
import { isCanyonV3Landscape, resolveLandscapeVersion } from '../render/Landscape';
import { SHARED_AERIAL_UNIFORMS } from '../render/AerialPerspective';
import { applyDebugLayers, defaultDebugLayers } from '../debug/DebugLayers';
import { ScenePanel } from '../debug/ScenePanel';
import { assemblyToRigTuning, loadAssemblyForTrack } from '../scene';
import { PerfHud } from '../debug/PerfHud';
import { installPerfDiag, perf } from '../debug/perf/PerfDiag';
import { PerfDiagPanel } from '../debug/perf/PerfDiagPanel';
import { installPerfToggles, perfToggles } from '../debug/perf/PerfToggles';
import { PerfLab, PerfLabPanel } from '../debug/perf/PerfLab';
import { collectSceneAudit } from '../debug/perf/SceneAudit';
import { GpuTimer, type PerfGpuTimer } from '../debug/perf/GpuTimer';
import { WebGPUGpuTimer } from '../debug/perf/WebGPUGpuTimer';
import { ensureIdentity, getIdentity, identityStorageScope, rerollGuestIdentityLocal, type Identity } from '../server/identity';
import { formatHandle } from '../server/handles';
import { isBackendConfigured } from '../server/supabase';
import {
  authCallbackSuffix,
  consumeAuthCallbackFlow,
  isSupabaseAuthCallback,
} from '../server/authRouting';
import { getCurrentUserEmail, savePilotProfile } from '../server/auth';
import {
  submitLap,
  fetchLeaderboard,
  fetchLapGhost,
  fetchOwnPersonalBest,
  type LeaderboardEntry,
} from '../server/laps';
import { submitLapIfEligible } from './lap-submission';
import {
  TitleScreen,
  normalizeRivalDifficulty,
  type PilotAccountStatus,
  type RivalDifficulty,
} from '../ui/Title';
import { PauseScreen } from '../ui/Pause';
import { SettingsScreen } from '../ui/Settings';
import { LeaderboardScreen } from '../ui/Leaderboard';
import { ClaimScreen } from '../ui/Claim';
import { CameraToggle } from '../ui/CameraToggle';
import { titleThemeMusic } from '../ui/TitleTheme';
import { raceMusic } from '../ui/RaceMusic';
import { MusicToggle } from '../ui/MusicToggle';
import { gameAudio } from '../audio/GameAudio';
import { AudioToggle } from '../ui/AudioToggle';
import { vibrateImpact } from './Haptics';
import { ScreenWakeLock } from './WakeLock';
import { mountDevDock } from '../ui/DevDock';
import { resolveMainDevRuntime } from '../ui/DevRouting';
import { applyLefty, leftyLayout } from '../ui/LeftyLayout';
import {
  resolveAsphaltLiveDetail,
  resolveAsphaltShaderMode,
  resolveTerrainShaderMode,
  resolveVolumetricCloudsEnabled,
} from '../boot/UrlParams';
import { loadBakedCrackDecals, loadBakedSurfaceAssets } from '../boot/BakedAssets';
import {
  NO_HIT,
  NO_SOFT_HIT,
  pickDominantHit,
  pickWallContact,
  sameWallNormal,
  wallContactSource,
  wallSlideSpeed,
} from './CollisionHelpers';
import { buildHudLeaderboardRows } from '../timing/HudLeaderboardRows';
import {
  deferVibeJamWidget,
  isKeyboardTextTarget,
  nextFrame,
  registerServiceWorker,
  setupTouchPauseButton,
  tryLockLandscape,
} from '../boot/BootHelpers';
import { launchDiagnosticRun } from '../boot/Diagnostics';

const PIX_DEFAULT_WIDTH = 480;
const PIX_DEFAULT_HEIGHT = 270;
let TRACK_URL = '/tracks/twin-suns-autodrome-arcade.csv';
const RACER_URL = '/racer-final.glb';
const SKY_URL = '/sky/twin-suns-sunset.webp';
const RACER_COLLISION_CLEARANCE = 2.25;
/** Effective half-width (m) of the racer chassis where it can graze a thin
 *  polyline barrier (rails/fences). Much smaller than RACER_COLLISION_CLEARANCE
 *  because polylines are thin — using the full clearance would teleport the
 *  racer when it crossed a rail line. */
const RACER_RAIL_SKIN = 0.6;
/** Lateral distance (m) past the corridor edge that triggers respawn. The
 *  corridor wall normally prevents this, so 60m is a safety net for glitches. */
const KILL_LATERAL_DISTANCE = 60;
/** Y depth below the spawn ground that triggers respawn (cliff fall). */
const KILL_Y_DEPTH = 30;
/** Slope angle (radians) above which off-track terrain blocks the racer.
 *  35° ≈ 0.61 rad — above this it's a wall, below it's a climbable hill. */
const MAX_TERRAIN_CLIMB_RAD = 0.61;
/** Seconds the screen fades to black before snapping to the safe point. */
const RESPAWN_FADE_S = 0.4;
/** Seconds after the snap during which collision resolvers no-op. */
const RESPAWN_INVULN_S = 1.0;
let TRACK_ID = 'twin-suns-autodrome';
const GAME_POD_GLOW_SCALE = 0.48;
const GHOST_POD_GLOW_SCALE = 0.22;
/** Spawn this far behind the start line so the player rolls under the
 *  gantry already moving. The lap timer holds until the first crossing. */
// Rollback enough that the racer reaches top speed (80 m/s) before
// crossing the start line. With accel=40 and drag=0.15 it takes ~2.4s
// and ~100m to wind up from rest. The lap timer is gated on the first
// start-line crossing so this wind-up isn't charged to lap 1, and ghost
// replays start from the same spawn so they hit the line at the same
// velocity as the player did — fair head-to-head comparison.
const START_ROLLBACK_METERS = 100;
const DRS_MIN_SCALE = DrsSystem.MIN_SCALE;
const DEV_FIXED_RENDER_SCALE = 1;
const RENDER_SCALE_MIN_PREVIEW = 0.5;
const RENDER_SCALE_PRESETS: Record<string, number> = {
  '1.00 fixed': 1,
  '0.95 preview': 0.95,
  '0.90 preview': 0.9,
  '0.85 DRS floor': DRS_MIN_SCALE,
  '0.75 stress': 0.75,
  '0.67 stress': 0.67,
  '0.50 stress': 0.5,
};
const RIVAL_COUNTDOWN_SECONDS = 3.2;
const RIVAL_PLAYER_GRID_LATERAL = 2.2;

function parseRenderScaleOverride(raw: string | null): number | null {
  if (!raw) return null;
  const parsed = Number.parseFloat(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return THREE.MathUtils.clamp(parsed, RENDER_SCALE_MIN_PREVIEW, 1);
}

type Mode = 'title' | 'racing' | 'paused';

export async function bootstrap(): Promise<void> {
  // Admin magic-link redirects fall back to the Site URL "/" when /admin isn't
  // in the Supabase allow-list, so admin auth payloads can land here. Both
  // admin and pilot sign-in flows mark their request before sending a link; the
  // newest fresh marker decides where this callback should be completed.
  const { search, hash } = window.location;
  if (isSupabaseAuthCallback(search, hash)) {
    const callbackFlow = consumeAuthCallbackFlow();
    if (callbackFlow === 'admin') {
      window.location.replace('/admin' + authCallbackSuffix(search, hash));
      return;
    }
  }

  const appEl = document.getElementById('app');
  if (!appEl) throw new Error('#app missing');
  const versionEl = document.getElementById('app-version');
  if (versionEl) versionEl.textContent = `v${__APP_VERSION__}`;

  // Bisect diagnostic: measure browser RAF cadence in isolation, before any
  // game code runs. PerfHud (created later) reads these values and surfaces
  // them in the on-screen line so the user can see at a glance whether a
  // "rAF 60" reading reflects the browser's actual cap or something we're
  // doing to throttle it ourselves.
  const { BareRafProbe } = await import('../debug/BareRafProbe');
  const screenWithRefresh = window.screen as Screen & { refreshRate?: number };
  const rafDiagnostics: { screenHz: number | null; bareRafHz: number | null } = {
    screenHz: typeof screenWithRefresh.refreshRate === 'number' ? screenWithRefresh.refreshRate : null,
    bareRafHz: null,
  };
  // eslint-disable-next-line no-console
  console.log('[diag] screen.refreshRate =', rafDiagnostics.screenHz ?? '(unavailable)');
  new BareRafProbe(60).measure().then((result) => {
    // eslint-disable-next-line no-console
    console.log(
      `[diag] bare RAF (startup): ${result.measuredHz.toFixed(1)} Hz (${result.meanDtMs.toFixed(2)}ms mean, ${result.samples} samples)`,
    );
    rafDiagnostics.bareRafHz = result.measuredHz;
    perfHud?.setRafDiagnostics(rafDiagnostics);
    // The startup probe runs while the main thread is loading Three.js,
    // parsing assets, and compiling pipelines — its number is noisy.
    // Re-run after 5s once the engine is warm; that reading is the real
    // steady-state browser RAF cap.
    window.setTimeout(() => {
      new BareRafProbe(120).measure().then((warm) => {
        // eslint-disable-next-line no-console
        console.log(
          `[diag] bare RAF (warm): ${warm.measuredHz.toFixed(1)} Hz (${warm.meanDtMs.toFixed(2)}ms mean, ${warm.samples} samples)`,
        );
        rafDiagnostics.bareRafHz = warm.measuredHz;
        perfHud?.setRafDiagnostics(rafDiagnostics);
      }).catch(() => {});
    }, 5000);
  }).catch(() => {});

  const params = new URLSearchParams(window.location.search);
  const autoRaceActive = params.has('autoRace');
  const rawOpponentRaceRequested = params.has('opponentRace') || Number.parseInt(params.get('opponents') ?? '0', 10) > 0;
  let opponentRaceEnabled = false;
  const opponentPreset = params.get('opponentProfile');
  let opponentDifficulty: RivalDifficulty = normalizeRivalDifficulty(opponentPreset ?? 'balanced');
  const autopilotPreset = params.get('autoRaceParams');
  const autopilotState = createAutopilotState(
    autopilotPreset === 'aggressive'
      ? AGGRESSIVE_AUTOPILOT_PARAMS
      : autopilotPreset === 'timid'
        ? TIMID_AUTOPILOT_PARAMS
        : undefined,
  );
  const services = new ServiceLocator();
  window.addEventListener('beforeunload', () => services.dispose(), { once: true });
  const requestedRenderHzCap = parseRenderFpsCap(params.get('fps'));
  let activeRenderHzCap = requestedRenderHzCap;
  let diagRenderHzOverride: number | null = null;
  const autoFrameCap = params.get('autoFps') !== 'off'
    && params.get('autoFrameCap') !== 'off'
    && Number.isFinite(requestedRenderHzCap)
    && requestedRenderHzCap > THERMAL_RENDER_FPS_CAP;
  const trackSelection = resolveTrackSelection(params);
  TRACK_URL = trackSelection.url;
  TRACK_ID = trackSelection.id;
  const landscapeVersion = resolveLandscapeVersion(params);
  const trackSystem = services.register(TrackSystem, new TrackSystem(trackSelection, landscapeVersion));
  const uiSystem = services.register(UISystem, new UISystem());
  const raceSystem = services.register(RaceSystem, new RaceSystem());
  const runtime = resolveMainDevRuntime(params, import.meta.env.DEV);
  if (runtime.route === 'worldReview') {
    const { startWorldReview } = await import('../debug/TrackReview');
    await startWorldReview({
      appEl,
      trackUrl: TRACK_URL,
      skyUrl: SKY_URL,
      racerUrl: RACER_URL,
      landscapeVersion,
    });
    return;
  }
  if (runtime.route === 'rigReview') {
    const { startRigReview } = await import('../debug/RigReview');
    await startRigReview({
      appEl,
      racerUrl: RACER_URL,
      skyUrl: SKY_URL,
      trackUrl: TRACK_URL,
      landscapeVersion,
    });
    return;
  }
  if (runtime.route === 'assetView') {
    const { startAssetView } = await import('../debug/AssetView');
    await startAssetView({
      appEl,
      skyUrl: SKY_URL,
    });
    return;
  }
  if (runtime.route === 'trackReview') {
    const { startTrackReview } = await import('../debug/TrackReview');
    await startTrackReview({ appEl, trackUrl: TRACK_URL });
    return;
  }
  const devMode = runtime.devMode;
  const opponentRaceAvailable = devMode;
  const opponentRaceRequested = opponentRaceAvailable && rawOpponentRaceRequested;
  opponentRaceEnabled = opponentRaceRequested;
  const canyonV3 = isCanyonV3Landscape(landscapeVersion);
  const activePreset = loadStyleForTrack(TRACK_URL);
  const sceneAssembly = loadAssemblyForTrack(TRACK_URL);
  const diagnosticRunRequested = params.get('diag') === 'full' || params.get('diag') === 'stress';
  const productionLikeRuntime = runtime.productionLike || !import.meta.env.DEV || diagnosticRunRequested;
  // Plain dev gameplay follows MAP's live editor overlay so edits can be
  // checked in GAME immediately. Production-like local runs (?prod,
  // ?production, ?playtest), production builds, and diagnostic runs always
  // use the saved preset on disk so they match what ships.
  const useStyleOverlay = !productionLikeRuntime;
  const styleConfig = useStyleOverlay ? activePreset.config : activePreset.saved;
  let resolvedStyle: ResolvedStyle = resolveStyle(styleConfig);
  // Match production by default in production-like local runs (?prod,
  // ?production, ?playtest). Plain Vite dev keeps preview terrain unless
  // ?terrain=full overrides.
  const terrainQuality = productionLikeRuntime || params.get('terrain') === 'full'
    ? 'full'
    : 'preview';
  // Baked asphalt/crack assets are part of the committed hero-3 look, not
  // just a production optimization. Dev GAME loads them too so the editor
  // previews the same road streaks as ?prod; keep explicit procedural opt-outs.
  const bakedCracksEnabled = params.get('cracks') !== 'procedural';
  const bakedSurfacesEnabled = params.get('surfaces') !== 'procedural'
    && params.get('surfaceBake') !== 'off';
  const bootStart = performance.now();
  const bootMarks: { label: string; ms: number }[] = [];
  const bootMark = (label: string): void => {
    const ms = performance.now() - bootStart;
    // Always record marks so ?diag=full can replay them in a report;
    // the dev-only console line is just noisy in production.
    bootMarks.push({ label, ms });
    if (!devMode) return;
    // eslint-disable-next-line no-console
    console.info(`[boot] ${label}: ${ms.toFixed(0)}ms`);
  };

  // Show the above-the-fold title shell before WebGL, track parsing, scenery,
  // network identity, or model loading. Those can all lag on cold mobile loads;
  // the player should still see the app immediately instead of a blank page.
  let bootReady = false;
  // Phase-aware boot label so the title screen status communicates what's
  // actually loading — "Preparing track..." is too vague when scene
  // assembly is fast but pod-racer GLB or shader compile is slow. Mirrors
  // what shipped racing games do: instant menu, status line counts up
  // through credible phases, Race button locks until ready.
  let bootPhase: 'init' | 'scene' | 'racer' | 'warmup' | 'ready' = 'init';
  let identity: Identity | null = null;
  let accountEmail: string | null = null;

  type Transitions = {
    toTitle: () => void;
    toRacing: () => void;
    toPaused: () => void;
    fromPaused: () => void;
    resetLap: () => void;
  };
  const t: Transitions = {
    toTitle: () => {},
    toRacing: () => {},
    toPaused: () => {},
    fromPaused: () => {},
    resetLap: () => {},
  };

  let settings: SettingsScreen | null = null;
  let leaderboard: LeaderboardScreen | null = null;
  let claim: ClaimScreen | null = null;
  let perfHud: PerfHud | null = null;
  let syncPilotScopedTiming = (): void => {};

  const title = new TitleScreen(document.body, {
    onRace: (raceMode, options) => {
      if (!bootReady) return;
      opponentRaceEnabled = opponentRaceAvailable && Boolean(options?.opponent);
      if (opponentRaceEnabled) {
        configureOpponentDifficulty(options?.opponentDifficulty ?? opponentDifficulty);
      }
      setRaceMode(raceMode);
      if (opponentRaceEnabled) void loadOpponentVisual();
      t.toRacing();
    },
    onLeaderboard: () => leaderboard?.show(),
    onClaim: () => { void claim?.show(); },
    onSettings: () => settings?.show(),
    onReroll: () => {
      // Update local + UI synchronously so the dice click feels instant.
      // Server sync runs in the background; if it fails the new pair sticks
      // locally and re-syncs on the next ensureIdentity() round-trip.
      const { handle, homeworld } = rerollGuestIdentityLocal();
      syncTitlePilot();
      void savePilotProfile(handle, homeworld);
    },
  });
  uiSystem.title = title;
  title.setOpponentRaceVisible(opponentRaceAvailable);
  title.setOpponentDifficulty(opponentDifficulty);
  title.setRaceEnabled(false);
  title.setPilot('...', 'Homeworld pending', false);
  title.setStatus('Preparing track...');
  titleThemeMusic.play();
  await nextFrame();

  const pilotAccountStatus = (id: Identity | null): PilotAccountStatus => {
    if (accountEmail) return 'claimed';
    return id?.userId ? 'linked' : 'guest';
  };
  const syncTitlePilot = (): void => {
    const id = identity ?? getIdentity();
    title.setPilot(
      id?.handle ?? '...',
      id?.homeworld ?? 'Homeworld pending',
      pilotAccountStatus(id),
    );
  };
  const refreshAccountEmail = async (): Promise<void> => {
    try {
      accountEmail = await getCurrentUserEmail();
    } catch {
      accountEmail = null;
    }
  };
  const BOOT_PHASE_TOTAL = 4;
  const bootPhaseProgress = (): { step: number; label: string } => {
    // Players watch this every cold load — keep the labels concrete so the
    // wait feels like meaningful work, not an unexplained pause.
    switch (bootPhase) {
      case 'init':   return { step: 0, label: 'Preparing track' };
      case 'scene':  return { step: 1, label: 'Building scene' };
      case 'racer':  return { step: 2, label: 'Loading pod racer' };
      case 'warmup': return { step: 3, label: 'Warming shaders' };
      case 'ready':  return { step: 4, label: 'Ready' };
    }
  };
  const updateTitleStatus = (): void => {
    const accountStatus = pilotAccountStatus(identity);
    if (!bootReady) {
      const { step, label } = bootPhaseProgress();
      title.setProgress({ step, total: BOOT_PHASE_TOTAL, label });
      title.setStatus(null);
      return;
    }
    title.setProgress(null);
    if (devMode) {
      title.setStatus('Dev mode - submissions disabled.');
    } else if (!isBackendConfigured()) {
      title.setStatus('Offline mode - leaderboard disabled (set Supabase env to enable).');
    } else if (accountStatus === 'claimed') {
      title.setStatus('Account claimed.');
    } else if (accountStatus === 'linked') {
      title.setStatus('This browser is linked to a claimed account.');
    } else {
      title.setStatus(null);
    }
  };

  const identityPromise = ensureIdentity().then((id) => {
    identity = id;
    return refreshAccountEmail().then(() => {
      syncTitlePilot();
      updateTitleStatus();
      return id;
    });
  }).catch((err) => {
    console.warn('[twin-suns-turbo] identity bootstrap failed', err);
    return null;
  });
  void identityPromise;

  const rendererHandle = await createRenderer(appEl);
  const { renderer } = rendererHandle;
  const webglRenderer = isWebGLRenderer(renderer) ? renderer : null;
  if (rendererHandle.fallbackReason) {
    bootMarks.push({ label: `renderer fallback: ${rendererHandle.fallbackReason}`, ms: performance.now() - bootStart });
  }
  // Surface WebGL context loss/restore in the boot timeline so a thermal
  // throttle event on mobile shows up in the diag report next to whatever
  // boot phase preceded it.
  renderer.domElement.addEventListener('webglcontextlost', (event) => {
    event.preventDefault();
    bootMark('webgl context lost');
  });
  renderer.domElement.addEventListener('webglcontextrestored', () => {
    bootMark('webgl context restored');
  });
  window.addEventListener('tst:webgpu-device-lost', (event) => {
    const detail = (event as CustomEvent).detail;
    bootMark('webgpu device lost');
    perf.event('webgpu.deviceLost', detail);
  });
  if (canyonV3) renderer.toneMappingExposure = 1.02;
  applyRendererStyle(renderer, resolvedStyle);
  // Dev HUD: top-bar PerfHud auto-mounts in dev mode (or ?perf) so the FPS /
  // DPR strip is one glance away while iterating. GPU timer queries are
  // diagnostic-only because they perturb the GPU pipeline, so they stay behind
  // ?perfDiag alongside the heavier phase table panel.
  const perfHudEnabled = (runtime.devMode || params.has('perf')) && !params.has('noperf');
  const diagModeRequested = params.get('diag');
  const diagRequested = diagModeRequested === 'full' || diagModeRequested === 'stress';
  const perfLabEnabled = params.has('perfLab') || diagRequested;
  const perfDiagEnabled = params.has('perfDiag') || perfLabEnabled;
  const postfxPassTrace = perfLabEnabled || params.has('postfxPassTrace');
  const gpuPostfxPassTrace = params.has('gpuPassTrace') || diagRequested;
  // GpuTimer chooses backend by renderer: WebGL2 uses
  // EXT_disjoint_timer_query_webgl2 for per-pass timing; WebGPU uses Three's
  // built-in timestamp-query support (renderer.info.render.timestamp via
  // resolveTimestampsAsync) for whole-frame timing. Both implement
  // PerfGpuTimer so PerfHud and PerfDiagPanel are backend-agnostic.
  let gpuTimer: PerfGpuTimer | null = null;
  if (perfDiagEnabled) {
    if (webglRenderer) {
      gpuTimer = new GpuTimer(webglRenderer);
    } else if (rendererHandle.isWebGPU && rendererHandle.capabilities.webgpuTimestamps) {
      gpuTimer = new WebGPUGpuTimer(rendererHandle.webgpuRenderer);
    }
  }
  // Helper: when gpuTimer is null (no ?perfDiag), call fn directly.
  // Without this, `gpuTimer?.measure(name, fn)` short-circuits to undefined
  // and the render never happens.
  const withGpuTimer = (name: string, fn: () => void): void => {
    if (gpuTimer) gpuTimer.measure(name, fn);
    else fn();
  };
  if (perfDiagEnabled) {
    installPerfToggles(window.location.search);
    installPerfDiag();
    perf.event('renderer.backend', {
      mode: rendererHandle.mode,
      requestedMode: rendererHandle.requestedMode,
      fallbackReason: rendererHandle.fallbackReason,
      webgpuTimestamps: rendererHandle.capabilities.webgpuTimestamps,
    });
    if (!gpuTimer) {
      console.warn('[perf] GPU timer unavailable; perf diagnostics will report CPU/frame timing only.');
    }
  }
  perfHud = perfHudEnabled ? new PerfHud(appEl, renderer, gpuTimer) : null;
  perfHud?.setRafDiagnostics(rafDiagnostics);
  const perfDiagPanel = perfDiagEnabled ? new PerfDiagPanel(appEl, gpuTimer) : null;
  const throttleShadowMaps = webglRenderer !== null && !devMode && !params.has('shadowAutoUpdate');
  if (throttleShadowMaps) {
    webglRenderer.shadowMap.autoUpdate = false;
    webglRenderer.shadowMap.needsUpdate = true;
  }
  bootMark(`renderer ready (${rendererHandle.mode})`);

  const scene = new THREE.Scene();
  const renderSystem = services.register(RenderSystem, new RenderSystem(rendererHandle, scene, gpuTimer));
  const { registerWebGPUCompatibility } = renderSystem;
  const diagnostics = services.register(DiagnosticsSystem, new DiagnosticsSystem());
  // Demonstrates the per-system registration pattern: RenderSystem
  // contributes its diagnostic snapshot under its own key, freshly
  // sampled on each window.__twin read. Legacy fields below are still
  // bulk-installed; future PRs migrate them one system at a time.
  diagnostics.registry.registerSnapshot('renderState', () => renderSystem.rendererDiagnostics());
  // Sky + fog + sun rig + environment are owned by SceneStyleRig — the SAME
  // function MAP and RIG call. By construction the three modes cannot drift
  // on lighting / sky / fog / shadows. Anything that varies per mode (camera,
  // racer, HUD, post-fx) lives outside this rig.
  const styleRig = bootSceneStyleRig({
    scene,
    renderer,
    styleConfig,
    resolvedStyle,
    canyonV3,
    webgpuPMREMGeneratorCtor: rendererHandle.webgpuPMREMGeneratorCtor,
  });
  const {
    sky, worldFog, sun, sunTarget, fillSun, fillSunTarget,
    hemisphere, bounceLight, bounceTarget, rimLight, rimTarget,
    shadowConfig, applyShadowConfig,
  } = styleRig;
  bootMark('lighting + environment ready');

  // Default to chase mode so GAME boots looking like MAP (the only camera
  // mode MAP exposes). The legacy top-down "tactical" view is still
  // reachable via the CameraToggle UI / C key, but it's no longer the
  // initial view since the player should land directly in the chase pose.
  const followCam = new FollowCamera(appEl.clientWidth / appEl.clientHeight, { mode: 'chase' });
  // Seed from Camera.defaults.json before overlaying any cached tuning. The
  // FollowCamera constructor's per-field defaults disagree with the committed
  // defaults JSON (e.g. chaseFovDeg 47 in code vs 60 in JSON), so without this
  // GAME boots at 47° while MAP boots at 60° and the views diverge visually.
  applyDefaultCameraTuning(followCam);
  applyCachedCameraTuning(followCam);
  const camera = followCam.camera;
  // Hook up the height sampler so chase mode hugs the terrain like the MAP
  // view (constant altitude above ground rather than above the racer).
  // `terrain` is built below; we wire it in after construction.

  const [track, sceneryKit, surfaceTextures, bakedCracks, bakedSurfaceAssets] = await Promise.all([
    loadTrack(TRACK_URL),
    canyonV3
      ? import('../render/SceneryAssets').then((module) => module.loadStartBridgeOnly())
      : import('../render/SceneryAssets').then((module) => module.loadSceneryHeroAssets()),
    canyonV3
      ? Promise.resolve(null)
      : import('../render/SurfaceTextures').then((module) => module.loadCanyonSurfaceTextures()),
    loadBakedCrackDecals(trackSelection.id, bakedCracksEnabled),
    loadBakedSurfaceAssets(trackSelection.id, bakedSurfacesEnabled),
  ]);
  trackSystem.setTrack(track);
  bootMark(canyonV3 ? 'track loaded' : 'track + scenery assets loaded');
  bootPhase = 'scene';
  updateTitleStatus();
  const terrainShaderMode = resolveTerrainShaderMode(params, bakedSurfaceAssets);
  const asphaltShaderMode = resolveAsphaltShaderMode(params, bakedSurfaceAssets);
  const asphaltLiveDetail = resolveAsphaltLiveDetail(params);
  // Production should never run the procedural shader path. If we get here
  // it means the baked manifest fetched but lacked a surface, OR the URL
  // forced ?surfaces=procedural / ?asphaltShader=detailed. Surface to perf
  // telemetry so a real-user degraded session is visible in dashboards.
  if (productionLikeRuntime) {
    if (terrainShaderMode === 'detailed' && !params.has('terrainShader') && !params.has('terrainMaterial')) {
      console.warn('[baked] terrain shader fell back to procedural in production; expect degraded perf');
      perf.event('bakedFallback', { surface: 'terrain', mode: terrainShaderMode });
    }
    if (asphaltShaderMode === 'detailed' && !params.has('asphaltShader') && !params.has('trackShader')) {
      console.warn('[baked] asphalt shader fell back to procedural in production; expect degraded perf');
      perf.event('bakedFallback', { surface: 'asphalt', mode: asphaltShaderMode });
    }
    if (bakedCracksEnabled && !bakedCracks) {
      console.warn('[baked] crack decals unavailable in production; using procedural canvas decals');
      perf.event('bakedFallback', { surface: 'crackDecals' });
    }
  }
  followCam.setTrack(track);
  const trackMeshes = buildTrackMeshes(track, surfaceTextures
    ? { asphaltTextures: surfaceTextures.asphalt, style: resolvedStyle, asphaltShaderMode, asphaltLiveDetail, bakedCracks, bakedSurfaceAssets, rendererMode: rendererHandle.mode }
    : { style: resolvedStyle, asphaltShaderMode, asphaltLiveDetail, bakedCracks, bakedSurfaceAssets, rendererMode: rendererHandle.mode });
  trackSystem.setMeshes(trackMeshes);
  bootMark('track meshes built');
  const terrain = buildTerrain(track, {
    version: landscapeVersion,
    sandTextures: surfaceTextures?.sand,
    quality: terrainQuality,
    shaderMode: terrainShaderMode,
    bakedSurface: bakedSurfaceAssets?.terrain ?? null,
    style: resolvedStyle,
    rendererMode: rendererHandle.mode,
  });
  followCam.sampleGroundHeight = terrain.sampleHeight;
  bootMark('terrain built');
  // Wrap the rock-solid static cluster in a WebGPU render bundle so the
  // backend records its draw commands once and replays them each frame as a
  // single cheap call. Scrape decals and sector decals stay loose because
  // their visibility/structure changes during a race. Kerbs and furniture
  // are static in membership; style swaps update material bindings in place
  // and invalidate the bundle below.
  // WebGPU render bundles currently replay stale camera-space state for the
  // track/start-area scene in chase view: the asphalt/start-line appears to
  // stick to the camera as the racer moves. Keep the production default on a
  // plain Group until that Three/BundleGroup path is proven with browser
  // parallax tests; `?staticBundles=on` remains as an explicit diagnostic
  // opt-in for perf experiments.
  const staticBundlesEnabled = params.get('staticBundles') === 'on';
  const bundleKerbsEnabled = params.get('bundleKerbs') !== 'off';
  const bundleFurnitureEnabled = params.get('bundleFurniture') !== 'off';
  const batchedMeshEnabled = !(rendererHandle.isWebGPU && isAndroidDevice()) && params.get('batchedMesh') !== 'off';
  const staticTrackBundle = createStaticBundle(
    staticBundlesEnabled ? rendererHandle.webgpuBundleGroupCtor : null,
  );
  staticTrackBundle.group.add(terrain.mesh);
  staticTrackBundle.group.add(trackMeshes.surface);
  staticTrackBundle.group.add(trackMeshes.sides);
  staticTrackBundle.group.add(trackMeshes.edgeLines);
  staticTrackBundle.group.add(trackMeshes.crackRelief);
  staticTrackBundle.group.add(trackMeshes.crackDecals);
  if (bundleKerbsEnabled) staticTrackBundle.group.add(trackMeshes.kerbs);
  scene.add(staticTrackBundle.group);
  if (!bundleKerbsEnabled) scene.add(trackMeshes.kerbs);
  scene.add(trackMeshes.sectorDecals);
  scene.add(trackMeshes.scrapeDecals);
  if (devMode) scene.add(trackMeshes.centerlineGuide);
  const trackFurniture = buildTrackFurniture(track, { style: resolvedStyle, useBatchedMesh: batchedMeshEnabled });
  if (bundleFurnitureEnabled) staticTrackBundle.group.add(trackFurniture.group);
  else scene.add(trackFurniture.group);
  const cloudField = buildCloudField(resolvedStyle, rendererHandle.mode);
  scene.add(cloudField.group);
  // Volumetric raymarched cloud dome — WebGPU only. Sits between the gradient
  // sky (renderOrder -1000) and the cloud cards (-450) so cards composite on
  // top for crisp parallax silhouettes near the player while the dome paints
  // a deep volumetric backdrop.
  //
  // Keep it opt-in via ?volumetricClouds=on / ?cloudDome=on. At 1862x941,
  // the 32-step raymarch + 4-tap shadow loop was the WebGPU native-DPR
  // regression by itself (~36ms scene GPU time vs ~4ms with the dome hidden).
  // The parallax cloud cards remain on by default and keep WebGPU/WebGL perf
  // comparisons about the renderer rather than an extra WebGPU-only effect.
  let volumetricClouds: VolumetricCloudDome | null = null;
  const volumetricCloudsEnabled = resolveVolumetricCloudsEnabled(params, {
    webgpu: rendererHandle.isWebGPU,
    mobile: isMobileDevice(),
  });
  if (volumetricCloudsEnabled) {
    volumetricClouds = createVolumetricCloudDome(resolvedStyle);
    scene.add(volumetricClouds.mesh);
  }
  const scenery = await buildScenery(track, {
    kit: sceneryKit,
    floorSampler: terrain.sampleHeight,
    version: landscapeVersion,
    canyonWallTextures: surfaceTextures?.sandstone,
    useBatchedMesh: batchedMeshEnabled,
  });
  // Scenery rocks are placed once at build time and never restructured during
  // a race, so they belong in the static bundle alongside the track surface.
  // applyEnvironmentStyle below only updates fog/sky uniforms; it doesn't
  // touch scenery materials, so no extra invalidation hook is needed here.
  staticTrackBundle.group.add(scenery.group);
  bootMark('scenery built');
  const sceneryObstacles: ObstacleObb[] = [...scenery.obstacles];
  // Triangle-mesh colliders are a fallback for authored GLBs that do not have
  // stable proxy obstacles. Normal bridge/scenery collision should use OBBs.
  const sceneryMeshColliders: MeshCollider[] = [...scenery.meshColliders];

  const cpTracker = trackSystem.buildCheckpointsFromTrack();
  const checkpoints = trackSystem.checkpoints!;

  const racer = new Racer({ showPlaceholder: false, rendererMode: rendererHandle.mode });
  raceSystem.racer = racer;
  racer.setTrack(track);
  const podRacerTuning = { ...assemblyToRigTuning(sceneAssembly), ...loadCachedPodRacerRigTuning() };
  racer.setPodRacerRigTuning(podRacerTuning);
  racer.setPodRacerRuntimeGlowScale(GAME_POD_GLOW_SCALE);
  scene.add(racer.group);
  const contactShadow = new ContactShadow({}, rendererHandle.mode);
  scene.add(contactShadow.mesh);
  const ghostShip = new GhostShip(undefined, undefined, undefined, rendererHandle.mode);
  raceSystem.ghostShip = ghostShip;
  ghostShip.racer.setTrack(track);
  ghostShip.racer.setPodRacerRigTuning(podRacerTuning);
  ghostShip.setGroundSampler((x, z) => (
    trackSurfaceHeightAtPosition(track, x, z)
    + (perfToggles.kerbY ? trackMeshes.kerbSurface.heightAt(x, z) : 0)
  ));
  ghostShip.racer.setPodRacerRuntimeGlowScale(GHOST_POD_GLOW_SCALE);
  scene.add(ghostShip.group);
  const profileForOpponentDifficulty = (difficulty: RivalDifficulty) => {
    if (difficulty === 'aggressive') return AGGRESSIVE_OPPONENT_PROFILE;
    if (difficulty === 'timid') return TIMID_OPPONENT_PROFILE;
    return undefined;
  };
  let opponentController = createOpponentControllerState(track, profileForOpponentDifficulty(opponentDifficulty));
  const opponentRacer = new Racer({ showPlaceholder: false, rendererMode: rendererHandle.mode });
  if (opponentRacer && opponentController) {
    opponentRacer.setTrack(track);
    opponentRacer.setPodRacerRigTuning({ ...podRacerTuning, engineGlowHue: 195, plasmaHueDeg: 190, coreHueDeg: 190 });
    opponentRacer.setPodRacerRuntimeGlowScale(GAME_POD_GLOW_SCALE);
    opponentRacer.group.visible = opponentRaceEnabled;
    placeOpponentAtStart(track, opponentController, opponentRacer.state, trackSurfaceHeightAtPosition(track, opponentController.lastSample.x, opponentController.lastSample.z));
    const renderOpponent = opponentRacer.render.bind(opponentRacer);
    opponentRacer.render = (alpha: number, frameDt?: number, clockTimeSeconds?: number): void => {
      opponentRacer.group.visible = opponentRaceEnabled;
      if (!opponentRaceEnabled || raceCountdownActive()) {
        opponentRacer.setPodRacerPreviewThrottle(0);
      }
      renderOpponent(alpha, frameDt, clockTimeSeconds);
    };
    scene.add(opponentRacer.group);
  }
  let opponentRenderFrames = 0;
  let opponentWrappedRenderFrames = 0;
  let opponentLastError: string | null = null;
  let opponentRuntimeProgress = 0;
  let opponentRuntimeSpeed = 0;
  let opponentRuntimeLaps = 0;
  let opponentRuntimeArmed = false;
  let opponentRuntimeLapStartMs = performance.now();
  let opponentRuntimeMinMargin = opponentController?.lastTrackMargin ?? 0;
  let opponentRuntimeMaxAbsLateral = opponentController?.maxAbsLateralThisLap ?? 0;
  const opponentRuntimeLapHistory: { lapTimeMs: number; minTrackMargin: number; maxAbsLateral: number }[] = [];
  const opponentRuntimeDiagnostics = () => {
    if (!opponentRaceEnabled || !opponentController) return [];
    const sample = sampleTrackAIPath(opponentController.path, opponentRuntimeProgress);
    return [{
      id: opponentController.profile.id,
      progress: opponentRuntimeProgress,
      speed: opponentRuntimeSpeed,
      laps: opponentRuntimeLaps,
      steps: opponentWrappedRenderFrames,
      lastTrackMargin: opponentRuntimeMinMargin,
      maxAbsLateralThisLap: opponentRuntimeMaxAbsLateral,
      minTrackMarginThisLap: opponentRuntimeMinMargin,
      lastLapMs: opponentRuntimeLapHistory.at(-1)?.lapTimeMs ?? null,
      targetSpeed: sample.targetSpeed * opponentController.profile.speedMultiplier,
      lapHistory: opponentRuntimeLapHistory.slice(),
    }];
  };
  const stepOpponentRuntime = (dt: number): void => {
    if (!opponentRaceEnabled || raceCountdownActive()) return;
    opponentWrappedRenderFrames++;
    perf.measure('opponent.ai', () => {
      const stepDt = Math.min(0.05, Math.max(0, dt));
      const sample = sampleTrackAIPath(opponentController.path, opponentRuntimeProgress);
      const targetSpeed = sample.targetSpeed * opponentController.profile.speedMultiplier;
      const delta = (targetSpeed >= opponentRuntimeSpeed ? opponentController.profile.acceleration : opponentController.profile.braking) * stepDt;
      opponentRuntimeSpeed = opponentRuntimeSpeed < targetSpeed
        ? Math.min(targetSpeed, opponentRuntimeSpeed + delta)
        : Math.max(targetSpeed, opponentRuntimeSpeed - delta);
      opponentRuntimeProgress += opponentRuntimeSpeed * stepDt;
      if (opponentRuntimeProgress >= opponentController.path.totalLength) {
        opponentRuntimeProgress %= opponentController.path.totalLength;
        const now = performance.now();
        if (opponentRuntimeArmed) {
          opponentRuntimeLaps += 1;
          opponentRuntimeLapHistory.push({
            lapTimeMs: now - opponentRuntimeLapStartMs,
            minTrackMargin: opponentRuntimeMinMargin,
            maxAbsLateral: opponentRuntimeMaxAbsLateral,
          });
          while (opponentRuntimeLapHistory.length > 10) opponentRuntimeLapHistory.shift();
        }
        opponentRuntimeArmed = true;
        opponentRuntimeLapStartMs = now;
        opponentRuntimeMinMargin = Number.POSITIVE_INFINITY;
        opponentRuntimeMaxAbsLateral = 0;
      }
      const nextSample = sampleTrackAIPath(opponentController.path, opponentRuntimeProgress);
      opponentRacer.beginStep();
      opponentRacer.state.position.set(nextSample.x, opponentRacer.state.position.y, nextSample.z);
      opponentRacer.state.velocity.set(nextSample.tx * opponentRuntimeSpeed, 0, nextSample.tz * opponentRuntimeSpeed);
      opponentRacer.state.heading = Math.atan2(nextSample.tx, nextSample.tz);
      opponentRacer.state.yawVelocity = 0;
      opponentRacer.state.position.y = trackSurfaceHeightAtPosition(
        track,
        opponentRacer.state.position.x,
        opponentRacer.state.position.z,
      ) + trackMeshes.kerbSurface.heightAt(opponentRacer.state.position.x, opponentRacer.state.position.z);
      const pos = locateTrackPosition(track, opponentRacer.state.position.x, opponentRacer.state.position.z);
      const trackMargin = pos.signedLateral >= 0 ? pos.widthLeft - pos.signedLateral : pos.widthRight + pos.signedLateral;
      opponentRuntimeMinMargin = Math.min(opponentRuntimeMinMargin, trackMargin);
      opponentRuntimeMaxAbsLateral = Math.max(opponentRuntimeMaxAbsLateral, Math.abs(pos.signedLateral));
      opponentRacer.setPodRacerPreviewThrottle(opponentRuntimeSpeed > 5 ? 1 : 0);
    });
  };
  const opponentGridPose = (): RaceStartPose => {
    const progress = opponentRaceEnabled
      ? rolledBackProgress(opponentController.path.totalLength, START_ROLLBACK_METERS)
      : 0;
    const sample = sampleTrackAIPath(opponentController.path, progress);
    return {
      x: sample.x,
      z: sample.z,
      heading: Math.atan2(sample.tx, sample.tz),
    };
  };
  const pinOpponentOnGrid = (): void => {
    const pose = opponentGridPose();
    const progress = opponentRaceEnabled
      ? rolledBackProgress(opponentController.path.totalLength, START_ROLLBACK_METERS)
      : 0;
    const groundY = trackSurfaceHeightAtPosition(track, pose.x, pose.z)
      + trackMeshes.kerbSurface.heightAt(pose.x, pose.z);
    opponentController.progress = progress;
    opponentController.speed = 0;
    opponentController.lastSample = sampleTrackAIPath(opponentController.path, progress);
    opponentRuntimeProgress = progress;
    opponentRuntimeSpeed = 0;
    pinRacerStateForStartHold(opponentRacer.state, pose, groundY);
    opponentRacer.beginStep();
    opponentRacer.setPodRacerPreviewThrottle(0);
  };
  const placeOpponentOnGrid = (): void => {
    pinOpponentOnGrid();
    opponentRuntimeLaps = 0;
    opponentRuntimeArmed = false;
    opponentRuntimeLapStartMs = performance.now();
    const pos = locateTrackPosition(track, opponentRacer.state.position.x, opponentRacer.state.position.z);
    const trackMargin = pos.signedLateral >= 0 ? pos.widthLeft - pos.signedLateral : pos.widthRight + pos.signedLateral;
    opponentController.lastTrackMargin = trackMargin;
    opponentController.maxAbsLateralThisLap = Math.abs(pos.signedLateral);
    opponentController.minTrackMarginThisLap = trackMargin;
    opponentRuntimeMinMargin = trackMargin;
    opponentRuntimeMaxAbsLateral = Math.abs(pos.signedLateral);
    opponentRuntimeLapHistory.length = 0;
    opponentRacer.setPodRacerPreviewThrottle(0);
  };
  const configureOpponentDifficulty = (difficulty: RivalDifficulty): void => {
    opponentDifficulty = difficulty;
    opponentController = createOpponentControllerState(track, profileForOpponentDifficulty(difficulty));
    placeOpponentOnGrid();
  };
  diagnostics.registry.registerSnapshot('opponents', opponentRuntimeDiagnostics);
  applyEnvironmentStyle(scene, resolvedStyle);
  let opponentVisualLoadStarted = false;
  let opponentVisualPromise: Promise<void> = Promise.resolve();
  const loadOpponentVisual = (): Promise<void> => {
    if (opponentVisualLoadStarted) return opponentVisualPromise;
    opponentVisualLoadStarted = true;
    opponentVisualPromise = opponentRacer.loadPodRacerAssembly(POD_RACER_ASSEMBLY_ASSETS).catch((err) => {
      console.warn('[opponent] pod racer assembly load failed; opponent remains hidden', err);
    });
    return opponentVisualPromise;
  };
  let racerVisualLoadStarted = false;
  let racerVisualPromise: Promise<void> = Promise.resolve();
  const loadRacerVisual = (): Promise<void> => {
    if (racerVisualLoadStarted) return racerVisualPromise;
    racerVisualLoadStarted = true;
    const racerLoad = racer.loadPodRacerAssembly(POD_RACER_ASSEMBLY_ASSETS).catch((err) => {
      console.warn('[racer] pod racer assembly load failed; racer remains hidden', err);
    });
    const ghostLoad = ghostShip.loadPodRacerAssembly(POD_RACER_ASSEMBLY_ASSETS).catch((err) => {
      console.warn('[ghost] pod racer assembly load failed; ghost proxy remains active', err);
    });
    const visualLoads = [racerLoad, ghostLoad];
    if (opponentRaceEnabled) visualLoads.push(loadOpponentVisual());
    racerVisualPromise = Promise.all(visualLoads).then(() => undefined);
    return racerVisualPromise;
  };
  const gameDebugLayers = defaultDebugLayers();
  let debugLayersDirty = true;
  let lastGameDebugLayerBits = -1;
  const gameDebugLayerBits = (): number => (
    (gameDebugLayers.clearToBlack ? 1 << 0 : 0)
    | (gameDebugLayers.sky ? 1 << 1 : 0)
    | (gameDebugLayers.keyLight ? 1 << 2 : 0)
    | (gameDebugLayers.fillLight ? 1 << 3 : 0)
    | (gameDebugLayers.hemisphere ? 1 << 4 : 0)
    | (gameDebugLayers.bounceLight ? 1 << 5 : 0)
    | (gameDebugLayers.rimLight ? 1 << 6 : 0)
    | (gameDebugLayers.environment ? 1 << 7 : 0)
    | (gameDebugLayers.fog ? 1 << 8 : 0)
    | (gameDebugLayers.emissive ? 1 << 9 : 0)
    | (gameDebugLayers.bloom ? 1 << 10 : 0)
    | (gameDebugLayers.colorGrade ? 1 << 11 : 0)
    | (gameDebugLayers.motionBlur ? 1 << 12 : 0)
    | (gameDebugLayers.toneMapping ? 1 << 13 : 0)
  );

  const startPose = getStartPose(track, START_ROLLBACK_METERS);
  // Start gate plane = centerline[0] with its forward tangent. Used below to
  // arm the lap timer when the racer first crosses it from behind.
  const startGate = {
    x: track.centerline[0].x,
    z: track.centerline[0].z,
    tx: track.tangents[0].tx,
    tz: track.tangents[0].tz,
  };
  let prevStartGateSign = -1;

  // Respawn state. `lastSafeRespawn` snapshots a recent on-track pose so we
  // can return the racer there after a kill-zone trigger (cliff fall, etc.).
  type RespawnState = 'live' | 'dying' | 'invuln';
  let respawnState: RespawnState = 'live';
  let respawnTimer = 0;
  const lastSafeRespawn = {
    x: startPose.x,
    z: startPose.z,
    heading: startPose.heading,
  };
  let safePointFrameCounter = 0;
  const spawnGroundY = trackSurfaceHeightAtPosition(track, startPose.x, startPose.z);
  const keyLightDir = { x: 0, y: 0, z: 0 };
  const fillLightDir = { x: 0, y: 0, z: 0 };
  const bounceLightDir = { x: 0, y: 0, z: 0 };
  const rimLightDir = { x: 0, y: 0, z: 0 };
  let opponentResetCount = 0;
  const playerGridPose = (): RaceStartPose => (
    opponentRaceEnabled ? gridPoseAtLateral(track, startPose, RIVAL_PLAYER_GRID_LATERAL) : startPose
  );
  const applyPlayerGridStart = (): void => {
    const pose = playerGridPose();
    racer.setStart(pose.x, pose.z, pose.heading);
  };
  const applyAutoRaceSpawnOverride = (): void => {
    if (!autoRaceActive || params.get('autoRaceSpawn') !== 'stuck') return;
    const pos = locateTrackPosition(track, startPose.x, startPose.z);
    const lateral = pos.widthLeft + Math.max(1, track.wallBuffer * 0.5);
    racer.setStart(
      pos.closestX + pos.nx * lateral,
      pos.closestZ + pos.nz * lateral,
      startPose.heading + Math.PI * 0.8,
    );
  };

  // Fade overlay used during respawn. Created once, kept hidden, opacity
  // driven by respawnTimer.
  const respawnFade = document.createElement('div');
  respawnFade.id = 'respawn-fade';
  respawnFade.style.cssText = [
    'position:fixed', 'inset:0', 'background:#000', 'opacity:0',
    'pointer-events:none', 'z-index:9999', 'transition:none',
  ].join(';');
  document.body.appendChild(respawnFade);
  const countdownOverlay = document.createElement('div');
  countdownOverlay.className = 'race-countdown';
  countdownOverlay.hidden = true;
  countdownOverlay.setAttribute('aria-live', 'assertive');
  countdownOverlay.innerHTML = [
    '<div class="race-countdown-card">',
    '<div class="race-countdown-label">Rival Launch</div>',
    '<div class="race-countdown-value">3</div>',
    '</div>',
  ].join('');
  document.body.appendChild(countdownOverlay);
  const countdownValueEl = countdownOverlay.querySelector<HTMLElement>('.race-countdown-value')!;
  let raceCountdownRemaining = 0;
  const raceCountdownActive = (): boolean => raceCountdownRemaining > 0;
  const updateCountdownOverlay = (): void => {
    if (!raceCountdownActive()) {
      countdownOverlay.hidden = true;
      return;
    }
    countdownOverlay.hidden = false;
    countdownValueEl.textContent = countdownLabel(raceCountdownRemaining);
  };
  const startRaceCountdown = (): void => {
    raceCountdownRemaining = opponentRaceEnabled ? RIVAL_COUNTDOWN_SECONDS : 0;
    updateCountdownOverlay();
  };
  const stepRaceCountdown = (dt: number): void => {
    if (!raceCountdownActive()) return;
    raceCountdownRemaining = Math.max(0, raceCountdownRemaining - dt);
    updateCountdownOverlay();
  };
  const clearRaceCountdown = (): void => {
    raceCountdownRemaining = 0;
    updateCountdownOverlay();
  };

  applyPlayerGridStart();
  applyAutoRaceSpawnOverride();
  racer.state.position.y = trackSurfaceHeightAtPosition(track, racer.state.position.x, racer.state.position.z)
    + trackMeshes.kerbSurface.heightAt(racer.state.position.x, racer.state.position.z);
  racer.render(1);
  followCam.resetTo(racer.state.position, racer.state.heading);
  updateSunRig(sun, sunTarget, racer.group.position, keyLightDirectionToTarget(resolvedStyle, keyLightDir), shadowConfig.forwardBias);
  updateSunRig(fillSun, fillSunTarget, racer.group.position, fillLightDirectionToTarget(resolvedStyle, fillLightDir));
  updateSunRig(bounceLight, bounceTarget, racer.group.position, directionFromAnglesToTarget(
    resolvedStyle.sunRig.bounce.elevationDeg,
    resolvedStyle.sunRig.bounce.azimuthDeg,
    100,
    bounceLightDir,
  ));
  updateSunRig(rimLight, rimTarget, racer.group.position, rimLightDirectionToTarget(resolvedStyle, camera, rimLightDir));
  sky.follow(camera.position);

  const particleRuntime = rendererHandle.isWebGPU
    ? { renderer: rendererHandle.webgpuRenderer, gpuTimer }
    : undefined;
  const dust = new DustTrail(sceneAssembly.layers.particles.dustTrail, rendererHandle.mode, particleRuntime);
  scene.add(dust.points);
  const engines = new EngineTrail(sceneAssembly.layers.particles.engineTrail, rendererHandle.mode, particleRuntime);
  scene.add(engines.group);
  const engineNozzles: THREE.Vector3[] = [];
  const engineIntensities: number[] = [0, 0];
  const plumeHeatPoints: Array<{ position: THREE.Vector3; radius: number }> = [];
  const scrapes = new ScrapeMarks(sceneAssembly.layers.particles.scrapeMarks, rendererHandle.mode);
  scene.add(scrapes.group);
  const scrapeEmitters: ScrapeEmitter[] = [];
  // Velocity-stretched ground streaks under the racer. The biggest single cue
  // for "speed" alongside the radial blur burst — short tan line segments
  // dragging backward along the racer's velocity vector. Spawn rate + length
  // both ramp via a smoothstep speed band so cruising gives gentle streaks
  // and top speed gives a dense rush.
  const groundStreaks = new GroundStreaks(sceneAssembly.layers.particles.groundStreaks, rendererHandle.mode);
  scene.add(groundStreaks.lines);

  await identityPromise;
  const currentStorageScope = (): string | null => identityStorageScope(identity ?? getIdentity());
  const lapTimer = new LapTimer({ storageScope: currentStorageScope() });
  raceSystem.lapTimer = lapTimer;
  const ghostRecorder = new GhostRecorder();
  // Buffer inputs during racing. The rolling start frames are discarded at
  // the timing gate; saved ghosts replay from the gate-crossing physics
  // state so later-lap PBs do not inherit an unrelated launch.
  ghostRecorder.start(FIXED_UPDATE_DT);
  let personalGhost: GhostRecording | null = loadGhost(TRACK_ID, PHYSICS_VERSION, null, currentStorageScope());
  const trainingGhost = buildTrainingGhost(track, TRACK_ID, PHYSICS_VERSION, {
    stepDt: FIXED_UPDATE_DT,
  });
  const localBestMs = (): number | null => (
    lapTimer.state.bestLap === null || !Number.isFinite(lapTimer.state.bestLap)
      ? null
      : Math.max(1, Math.round(lapTimer.state.bestLap))
  );
  const localPersonalGhostForMs = (totalMs: number | null | undefined): GhostRecording | null => (
    ghostMatchesLap(personalGhost, totalMs) ? personalGhost : null
  );
  const matchingLocalPersonalGhost = (): GhostRecording | null => localPersonalGhostForMs(localBestMs());
  const adaptiveTrainingRabbit = new AdaptiveTrainingRabbit(track);
  const isTrainingGhostRecording = (rec: GhostRecording | null): boolean => rec === trainingGhost;
  const trainingRaceTarget = (): RaceTargetState => ({
    label: 'Training Rabbit',
    totalMs: null,
    note: localBestMs() === null ? 'Set a clean lap' : 'Warm-up target',
  });
  const personalRaceTarget = (totalMs: number | null = localBestMs()): RaceTargetState => ({
    label: 'Personal Best',
    totalMs,
    note: totalMs === null ? 'Set a clean lap' : 'Beat PB',
  });
  const fallbackRaceTarget = (): RaceTargetState => (
    localBestMs() === null
      ? trainingRaceTarget()
      : personalRaceTarget()
  );
  const targetLabelForLeaderboardRow = (row: LeaderboardEntry): string => {
    const name = formatHandle(row.handle);
    return row.rank > 0 ? `#${row.rank} ${name}` : name;
  };
  const leaderboardRaceTarget = (row: LeaderboardEntry): RaceTargetState => ({
    label: targetLabelForLeaderboardRow(row),
    totalMs: Math.max(1, Math.round(row.total_ms)),
  });
  const raceTargetForGhost = (
    rec: GhostRecording | null,
    label: string,
    title: string,
  ): RaceTargetState => {
    if (isTrainingGhostRecording(rec)) return trainingRaceTarget();
    if (title === 'Personal Ghost' || label === 'Personal Best') {
      return personalRaceTarget(rec ? Math.max(1, Math.round(rec.lapMs)) : localBestMs());
    }
    if (rec) {
      return {
        label: label || 'Target Ghost',
        totalMs: Math.max(1, Math.round(rec.lapMs)),
      };
    }
    return fallbackRaceTarget();
  };
  const setTrainingGhostTarget = (): void => {
    setDesiredGhost(trainingGhost, 'Training Ghost', 'Training Ghost', trainingRaceTarget());
  };
  const wallContactCache = {
    active: false,
    nx: 0,
    nz: 0,
    seconds: 0,
  };
  // Single source of truth for "what is the racer doing against a wall right
  // now" — drives audio, particles, camera, rumble, post-fx so secondary
  // effects move together as one event.
  const contactState: ContactState = makeContactState();
  const _preCollisionVelocity = { x: 0, z: 0 };
  const initialPersonalGhost = matchingLocalPersonalGhost();
  // If the local PB ghost is unavailable (e.g. PHYSICS_VERSION just bumped and
  // dropped the stored recording, but the saved PB time survived), fall back
  // to the training rabbit. Never start a session with `null` — empty track is
  // worse than the wrong target.
  const initialGhost = initialPersonalGhost ?? trainingGhost;
  let activeGhost: GhostRecording | null = initialGhost;
  let desiredGhost: GhostRecording | null = initialGhost;
  let activeGhostTitle = initialPersonalGhost
    ? 'Personal Ghost'
    : initialGhost === trainingGhost
      ? 'Training Ghost'
      : 'Target Ghost';
  let desiredGhostTitle = activeGhostTitle;
  let activeGhostLabel = initialPersonalGhost
    ? 'Personal Best'
    : initialGhost === trainingGhost
      ? 'Training Ghost'
      : '';
  let desiredGhostLabel = activeGhostLabel;
  let activeRaceTarget = raceTargetForGhost(activeGhost, activeGhostLabel, activeGhostTitle);
  let desiredRaceTarget = activeRaceTarget;
  ghostShip.bind(activeGhost);
  ghostShip.setVisualMode(isTrainingGhostRecording(activeGhost) ? 'rabbit' : 'ghost');
  if (isTrainingGhostRecording(activeGhost)) {
    ghostShip.placeAtState(adaptiveTrainingRabbit.reset(0));
  }
  let ghostReplayActive = false;
  let liveLapFixedMs: number | null = null;
  let liveLapProgress: number | null = null;
  let prevLiveLapArc: number | null = null;
  let latestGhostDeltaMs: number | null = null;
  let ghostTargetRequest = 0;
  const makeGhostProgressSamples = (rec: GhostRecording | null): GhostProgressSample[] => (
    rec
      ? buildGhostProgressSamples(
        rec,
        (state) => locateTrackPosition(track, state.x, state.z).arcLength,
        track.totalLength,
      )
      : []
  );
  let ghostProgressSamples = makeGhostProgressSamples(isTrainingGhostRecording(activeGhost) ? null : activeGhost);
  let pbProgressSamples = makeGhostProgressSamples(personalGhost);
  const setPersonalGhost = (rec: GhostRecording | null): void => {
    personalGhost = rec;
    pbProgressSamples = makeGhostProgressSamples(rec);
  };
  const hud = new HUD(lapTimer);
  uiSystem.hud = hud;
  const bindActiveGhost = (
    rec: GhostRecording | null,
    label: string,
    title = 'Target Ghost',
    raceTarget: RaceTargetState = raceTargetForGhost(rec, label, title),
  ): void => {
    activeGhost = rec;
    activeGhostTitle = title;
    activeGhostLabel = label;
    activeRaceTarget = raceTarget;
    ghostShip.bind(activeGhost);
    ghostShip.setVisualMode(isTrainingGhostRecording(activeGhost) ? 'rabbit' : 'ghost');
    const useFixedComparison = activeGhost !== null && !isTrainingGhostRecording(activeGhost);
    ghostProgressSamples = makeGhostProgressSamples(useFixedComparison ? activeGhost : null);
    if (isTrainingGhostRecording(activeGhost)) {
      ghostShip.placeAtState(adaptiveTrainingRabbit.reset(liveLapProgress ?? 0));
    }
    const targetSectors = useFixedComparison && activeGhost
      ? buildGhostSectorTimes(activeGhost, ghostProgressSamples, track.totalLength)
      : null;
    hud.setTargetSplits(
      targetSectors,
      title === 'Personal Ghost' ? 'Personal Splits' : 'Target Splits',
    );
    hud.setRaceTarget(activeRaceTarget);
    latestGhostDeltaMs = null;
    hud.setGhostTracker(null);
  };
  hud.setRaceTarget(activeRaceTarget);
  const ghostMarkerWorldPosition = new THREE.Vector3();
  const progressRelativeToStart = (arc: number): number => (
    arc > track.totalLength * 0.5 ? arc - track.totalLength : arc
  );
  const rivalGapMs = (): number | null => {
    if (opponentRuntimeSpeed <= 1) return null;
    const playerProgress = lapTimer.state.running && liveLapProgress !== null
      ? liveLapProgress
      : progressRelativeToStart(locateTrackPosition(track, racer.state.position.x, racer.state.position.z).arcLength);
    const rivalProgress = opponentRuntimeArmed
      ? opponentRuntimeProgress
      : progressRelativeToStart(opponentRuntimeProgress);
    let progressDelta = rivalProgress - playerProgress;
    if (progressDelta > track.totalLength * 0.5) progressDelta -= track.totalLength;
    else if (progressDelta < -track.totalLength * 0.5) progressDelta += track.totalLength;
    const playerSpeed = racerSpeed(racer.state);
    const speedRef = Math.max(12, (playerSpeed + opponentRuntimeSpeed) * 0.5);
    return (progressDelta / speedRef) * 1000;
  };
  const syncGhostHud = (): void => {
    if (opponentRaceEnabled) {
      let screen = null;
      if (opponentRacer.group.visible) {
        opponentRacer.group.getWorldPosition(ghostMarkerWorldPosition);
        const canvas = renderer.domElement;
        screen = projectGhostMarkerToScreen(
          ghostMarkerWorldPosition,
          camera,
          {
            width: canvas.clientWidth || window.innerWidth,
            height: canvas.clientHeight || window.innerHeight,
          },
        );
      }
      hud.setGhostTracker({
        title: 'Rival',
        label: 'Rival',
        deltaMs: rivalGapMs(),
        screen,
      });
      return;
    }
    if (!activeGhost || !ghostReplayActive) {
      hud.setGhostTracker(null);
      return;
    }
    let screen = null;
    if (!ghostShip.isFinished() && ghostShip.group.visible) {
      ghostShip.group.getWorldPosition(ghostMarkerWorldPosition);
      const canvas = renderer.domElement;
      screen = projectGhostMarkerToScreen(
        ghostMarkerWorldPosition,
        camera,
        {
          width: canvas.clientWidth || window.innerWidth,
          height: canvas.clientHeight || window.innerHeight,
        },
      );
    }
    hud.setGhostTracker({
      title: activeGhostTitle,
      label: activeGhostLabel,
      deltaMs: latestGhostDeltaMs,
      screen,
    });
  };
  const minimap = new Minimap(document.body, track, checkpoints);
  uiSystem.minimap = minimap;
  minimap.hide();
  const wrongWay = new WrongWayDetector(track);
  const offTrackBudget = new OffTrackBudget();
  raceSystem.wrongWay = wrongWay;
  raceSystem.offTrackBudget = offTrackBudget;

  const basePhysics = activePhysics();
  const physics = { ...basePhysics };
  raceSystem.physics = physics;
  const collisionDebug = {
    active: false,
    source: 'none',
    impactSpeed: 0,
    slideSpeed: 0,
    contactSeconds: 0,
    normalX: 0,
    normalZ: 0,
  };
  // Race-mode physics overlays (beginner / assisted) are dormant: GAME drives
  // the same raw activePhysics() the MAP view does, so the two read 1:1.
  // Title screen still reports the chosen mode for HUD/leaderboard flags.
  let currentRaceMode: RaceMode = 'standard';
  const setRaceMode = (raceMode: RaceMode): void => {
    currentRaceMode = raceMode;
    raceSystem.setRaceMode(raceMode);
  };
  const pixelation = new PixelationPipeline({
    width: PIX_DEFAULT_WIDTH,
    height: PIX_DEFAULT_HEIGHT,
    enabled: false,
  });
  // Bloom is the dominant per-frame post-fx cost on mobile (~2-3ms of CPU
  // dispatch + multi-pass GPU work). The diag scenario `scene.shadows-off`
  // showed phones lose ~3ms p95 on shadows; bloom is in the same league.
  // Default-off on touch devices, restorable with ?bloom=on. Desktop keeps
  // bloom on; ?nobloom remains the universal opt-out.
  const isMobileLikely = ('ontouchstart' in window) || (navigator.maxTouchPoints ?? 0) > 1;
  const bloomDefault = !isMobileLikely;
  const bloomEnabled = params.has('bloom')
    ? params.get('bloom') !== 'off'
    : (!params.has('nobloom') && bloomDefault);
  const postFxOptions: SfxOptions = {
    bloom: {
      intensity: resolvedStyle.sfx.bloom.intensity,
      radius: resolvedStyle.sfx.bloom.radius,
      threshold: resolvedStyle.sfx.bloom.threshold,
    },
    selectiveBloom: resolvedStyle.sfx.selectiveBloom,
    colorGrade: {
      enabled: resolvedStyle.sfx.colorGrade.enabled,
      lift: resolvedStyle.sfx.colorGrade.lift,
      gain: resolvedStyle.sfx.colorGrade.gain,
      saturation: resolvedStyle.sfx.colorGrade.saturation,
      contrast: resolvedStyle.sfx.colorGrade.contrast,
      gamma: resolvedStyle.sfx.colorGrade.gamma,
    },
    // Always wire the radial-blur pass; the per-frame strength reflects
    // motionBlurPrefs.enabled + racer speed. When the pref is off the
    // strength stays 0 and the shader fast-paths to a single tap.
    motion: {
      samples: resolvedStyle.sfx.motion.radialSamples,
      strength: 0,
      falloff: resolvedStyle.sfx.motion.falloff,
      chromaticAmount: resolvedStyle.sfx.motion.chromaticAmount,
    },
    pixelation: {
      enabled: pixelation.config.enabled,
      width: pixelation.config.width,
      height: pixelation.config.height,
    },
    vignette: resolvedStyle.sfx.vignette,
    chromaticAberration: resolvedStyle.sfx.chroma,
    heat: { intensity: 0 },
  };
  let bloom: SfxRuntime | null = null;
  if (bloomEnabled && webglRenderer) {
    bloom = new SfxPipeline(webglRenderer, scene, camera, postFxOptions);
  } else if (rendererHandle.isWebGPU) {
    const { WebGPUSfxPipeline } = await import('../render/SfxWebGPU');
    bloom = new WebGPUSfxPipeline(rendererHandle.webgpuRenderer, scene, camera, postFxOptions);
    bloom.setBloomEnabled(bloomEnabled);
  }
  const postFx = services.register(PostFXSystem, new PostFXSystem(pixelation, bloom));
  // Per-pass GPU timing inside the postfx composer was tried but proved
  // unreliable: each TIME_ELAPSED query forces a GPU sync that breaks the
  // GPU's normal pipelining, so 7 queries inflate the measurement ~4x.
  // We keep the single outer wrap below (one query per frame) instead.
  const adaptiveRenderQuality = !params.has('noAdaptiveQuality')
    && params.get('adaptiveQuality') !== 'off'
    && (runtime.productionLike || !import.meta.env.DEV || params.has('adaptiveQuality'));
  const stylePrewarmEnabled = params.get('prewarmStyle') !== 'off' && !params.has('noStylePrewarm');
  let reducedRenderLoad = false;
  let reducedRenderLoadAt: number | null = null;
  let reducedRenderLoadReason: string | null = null;
  // Boot warmup: the first few seconds after racing starts include asset
  // upload, shader compile, and texture decode spikes that produce huge frame
  // times unrelated to sustained render cost. Without this, adaptive reducer
  // fires on a single slow boot frame and drops transient post-fx forever —
  // divergence v MAP view, where there's no adaptive reducer at all.
  const ADAPTIVE_WARMUP_FRAMES = 120;
  const adaptiveQualityGate = createAdaptiveQualityGate({
    enabled: adaptiveRenderQuality,
    warmupFrames: ADAPTIVE_WARMUP_FRAMES,
    slowFrameSeconds: 0.08,
    sustainedFrames: 3,
  });
  // Production-like play is allowed to reduce the highest-cost transient
  // features after DRS is already stuck at its visual floor. Plain dev keeps
  // this opt-in so MAP/GAME visual comparisons do not silently drift.
  const reduceRenderLoad = (reason: string): void => {
    if (reducedRenderLoad) return;
    reducedRenderLoad = true;
    reducedRenderLoadAt = performance.now();
    reducedRenderLoadReason = reason;
    postFx.reduceLoad();
    motionBlurPrefs.liveStrength = 0;
    gpuTimer?.reset();
    perfHud?.setQualityState('reduced');
    perf.event('renderQualityReduced', { reason, tMs: reducedRenderLoadAt });
    if (devMode) {
      console.info(`[render] reduced quality after ${reason}: motion blur reduced, DPR and shadows preserved`);
    }
  };
  const applyResolvedSceneStyle = (): void => {
    applyRendererStyle(renderer, resolvedStyle);
    applyLiveSkyStyle(sky, resolvedStyle);
    applySkySceneStyle(renderer, scene, worldFog, sky.horizonColor, resolvedStyle);
    applySunRigStyle(sun, fillSun, hemisphere, bounceLight, rimLight, resolvedStyle);
    applyEnvironmentStyle(scene, resolvedStyle);
    updateSunRig(sun, sunTarget, racer.group.position, keyLightDirectionToTarget(resolvedStyle, keyLightDir), shadowConfig.forwardBias);
    updateSunRig(fillSun, fillSunTarget, racer.group.position, fillLightDirectionToTarget(resolvedStyle, fillLightDir));
    updateSunRig(bounceLight, bounceTarget, racer.group.position, directionFromAnglesToTarget(
      resolvedStyle.sunRig.bounce.elevationDeg,
      resolvedStyle.sunRig.bounce.azimuthDeg,
      100,
      bounceLightDir,
    ));
    updateSunRig(rimLight, rimTarget, racer.group.position, rimLightDirectionToTarget(resolvedStyle, camera, rimLightDir));
    debugLayersDirty = true;
    applyCloudUniforms(
      cloudField.material,
      resolvedStyle.derivedLighting,
      resolvedStyle.clouds.shading.cloudBaseHex,
      resolvedStyle.clouds.shading.wrap,
      resolvedStyle.clouds.shading.rimStrength,
    );
    if (volumetricClouds) volumetricClouds.applyStyle(resolvedStyle);
  };
  let runtimeStylePrimed = false;
  const applyRuntimeStyle = (nextStyle: StyleConfig): void => {
    Object.assign(styleConfig, nextStyle);
    resolvedStyle = resolveStyle(styleConfig);
    trackMeshes.applyStyle(resolvedStyle);
    terrain.applyStyle(resolvedStyle);
    trackFurniture.applyStyle(resolvedStyle);
    cloudField.applyStyle(resolvedStyle);
    // Style transitions can swap baked-PBR maps and asphalt-shader textures
    // on the bundled meshes. Material instance identity is preserved (we
    // rebind .map / .normalMap / userData uniforms in place), but the
    // texture bindings recorded inside the bundle are tied to the old
    // GPU resources — re-record so the new bindings take effect.
    staticTrackBundle.invalidate();
    applyResolvedSceneStyle();
    postFx.applyStyle(resolvedStyle);
    if (runtimeStylePrimed) {
      renderSystem.prewarmAfterStyleChange(camera, { enabled: stylePrewarmEnabled, reason: 'runtime-style' });
    } else {
      runtimeStylePrimed = true;
    }
  };
  // Push the initial resolved style through every uniform path. The MAP view
  // (src/debug/TrackReview.ts:450) relies on its StylePanel onChange to push
  // these on first edit, but for parity we apply them once at boot so the
  // first GAME frame matches MAP — asphalt shader uniforms, terrain palette,
  // furniture/cloud uniforms, sky, fog, env, sun rig, and bloom config all
  // pulled from the same `resolvedStyle` snapshot used to build the meshes.
  applyRuntimeStyle(styleConfig);
  // Subscribe to style change events so live edits in any panel (MAP,
  // TuningPanel, RIG) propagate to the GAME loop the same way they do to
  // MAP. Mirrors src/debug/TrackReview.ts:571.
  const stopGameStyleListener = listenToStyleConfigChange(applyRuntimeStyle);
  window.addEventListener('beforeunload', stopGameStyleListener, { once: true });
  // Runtime debug layers are applied only when a style/debug toggle changes;
  // applying them every frame needlessly pokes renderer/post-fx state.
  bootMark('post effects ready');

  // Leaf screens (no transition refs needed).
  const cameraToggle = new CameraToggle(document.body, followCam.mode, {
    onToggle: (mode) => followCam.setMode(mode),
  });
  uiSystem.cameraToggle = cameraToggle;
  uiSystem.musicToggle = new MusicToggle(document.body);
  uiSystem.audioToggle = new AudioToggle(document.body);
  settings = new SettingsScreen(document.body, pixelation, {
    onClose: () => settings?.hide(),
  });
  uiSystem.settings = settings;
  leaderboard = new LeaderboardScreen(document.body, TRACK_ID, {
    onClose: () => leaderboard?.hide(),
  });
  uiSystem.leaderboard = leaderboard;
  claim = new ClaimScreen(document.body, {
    onClose: () => claim?.hide(),
    onClaimed: () => {
      void (async () => {
        const id = getIdentity();
        if (id) identity = id;
        await refreshAccountEmail();
        syncTitlePilot();
        syncPilotScopedTiming();
        void refreshTimingBoard();
        const accountStatus = pilotAccountStatus(id);
        title.setStatus(
          accountStatus === 'claimed'
            ? 'Account claimed. Future laps will save under your handle.'
            : accountStatus === 'linked'
              ? 'This browser is linked to a claimed account.'
              : 'Pilot profile saved.',
        );
      })();
    },
  });
  uiSystem.claim = claim;

  const pause = new PauseScreen(document.body, {
    onResume: () => t.fromPaused(),
    onReset: () => {
      t.resetLap();
      t.fromPaused();
    },
    onLeaderboard: () => leaderboard?.show(),
    onSettings: () => settings?.show(),
    onQuitToTitle: () => t.toTitle(),
  });
  uiSystem.pause = pause;

  const devDock = devMode
    ? mountDevDock(document.body, {
        initialView: runtime.initialDockView,
      })
    : null;

  {
    const id = getIdentity();
    if (id) identity = id;
    syncTitlePilot();
  }
  updateTitleStatus();

  let mode: Mode = 'title';

  const applyDesiredGhost = (): void => {
    bindActiveGhost(desiredGhost, desiredGhostLabel, desiredGhostTitle, desiredRaceTarget);
  };

  const setDesiredGhost = (
    rec: GhostRecording | null,
    label: string,
    title = 'Target Ghost',
    raceTarget: RaceTargetState = raceTargetForGhost(rec, label, title),
  ): void => {
    desiredGhost = rec;
    desiredGhostLabel = label;
    desiredGhostTitle = title;
    desiredRaceTarget = raceTarget;
    if (mode !== 'racing' || !lapTimer.state.running) {
      applyDesiredGhost();
    }
  };

  const syncLeaderboardGhostTarget = async (
    row: LeaderboardEntry | null,
    id: Identity | null,
    rows: LeaderboardEntry[] = [],
    options: { includeSlowerFallback?: boolean; personalBestFallback?: boolean; personalBestFallbackTitle?: string } = {},
  ): Promise<void> => {
    const requestId = ++ghostTargetRequest;
    const personalBestFallback = options.personalBestFallback ?? true;
    if (row && isOwnLeaderboardRow(row, id)) {
      const localGhost = localPersonalGhostForMs(row.total_ms);
      if (localGhost) {
        setDesiredGhost(localGhost, 'Personal Best', 'Personal Ghost', personalRaceTarget(row.total_ms));
        return;
      }

      if (row.has_ghost) {
        const serverGhost = await fetchLapGhost(row.id, TRACK_ID, PHYSICS_VERSION);
        if (requestId !== ghostTargetRequest) return;
        if (serverGhost && ghostMatchesLap(serverGhost, row.total_ms)) {
          setPersonalGhost(serverGhost);
          saveGhost(serverGhost, currentStorageScope());
          setDesiredGhost(serverGhost, 'Personal Best', 'Personal Ghost', personalRaceTarget(row.total_ms));
          return;
        }
      }

      setDesiredGhost(null, '', 'Target Ghost', personalRaceTarget(row.total_ms));
      return;
    }

    const ownRow = id ? rows.find((candidate) => isOwnLeaderboardRow(candidate, id)) ?? null : null;
    const hasMatchingLocalPersonalGhost = ownRow
      ? localPersonalGhostForMs(ownRow.total_ms) !== null
      : ghostMatchesLap(personalGhost, localBestMs());
    const candidates = getLeaderboardGhostCandidates(row, rows, id, {
      excludeOwn: hasMatchingLocalPersonalGhost,
      includeSlowerFallback: options.includeSlowerFallback,
    });
    for (const candidate of candidates) {
      const targetGhost = await fetchLapGhost(candidate.id, TRACK_ID, PHYSICS_VERSION);
      if (requestId !== ghostTargetRequest) return;
      if (targetGhost) {
        const ownTarget = isOwnLeaderboardRow(candidate, id);
        setDesiredGhost(
          targetGhost,
          ownTarget ? 'Personal Best' : formatHandle(candidate.handle),
          ownTarget ? 'Personal Ghost' : 'Target Ghost',
          ownTarget ? personalRaceTarget(candidate.total_ms) : leaderboardRaceTarget(candidate),
        );
        return;
      }
    }

    let fallbackGhost: GhostRecording | null = ownRow
      ? localPersonalGhostForMs(ownRow.total_ms)
      : matchingLocalPersonalGhost();
    if (personalBestFallback && !fallbackGhost && ownRow?.has_ghost) {
      const serverGhost = await fetchLapGhost(ownRow.id, TRACK_ID, PHYSICS_VERSION);
      if (requestId !== ghostTargetRequest) return;
      if (serverGhost && ghostMatchesLap(serverGhost, ownRow.total_ms)) {
        setPersonalGhost(serverGhost);
        saveGhost(serverGhost, currentStorageScope());
        setDesiredGhost(serverGhost, 'Personal Best', 'Personal Ghost', personalRaceTarget(ownRow.total_ms));
        return;
      }
    }
    // Final fallback: when none of the leaderboard ghosts loaded (e.g. the
    // next-rank rival flagged "no ghost") and we couldn't pin a ghost to
    // the player's leaderboard PB, race against whatever local PB ghost we
    // do have. Better to chase your own ghost than have the track empty.
    if (personalBestFallback && !fallbackGhost && personalGhost) {
      fallbackGhost = personalGhost;
    }
    if (!fallbackGhost) {
      // No matching ghost anywhere — local PB ghost may have been invalidated
      // (e.g. by a PHYSICS_VERSION bump) even if the saved PB time survived.
      // Run the training rabbit so the track is never empty.
      setTrainingGhostTarget();
      return;
    }
    setDesiredGhost(
      fallbackGhost,
      personalBestFallback ? 'Personal Best' : '',
      personalBestFallback
        ? (options.personalBestFallbackTitle ?? 'Personal Ghost')
        : 'Target Ghost',
      personalBestFallback
        ? personalRaceTarget(Math.max(1, Math.round(fallbackGhost.lapMs)))
        : fallbackRaceTarget(),
    );
  };

  const syncCameraHudMode = (): void => {
    document.body.classList.toggle('is-camera-tactical', followCam.mode === 'topDown');
    document.body.classList.toggle('is-camera-chase', followCam.mode === 'chase');
  };
  syncCameraHudMode();

  const inputSystem = services.register(InputSystem, new InputSystem(params, {
    onAnyStart: () => { /* race begins via Race button, not first input */ },
    onReset: () => {
      if (mode !== 'racing') return;
      t.resetLap();
    },
    onPauseToggle: () => {
      if (mode === 'racing') t.toPaused();
      else if (mode === 'paused') t.fromPaused();
    },
    onCameraToggle: () => {
      if (mode !== 'racing') return;
      const nextMode = followCam.mode === 'topDown' ? 'chase' : 'topDown';
      followCam.setMode(nextMode);
      cameraToggle.setMode(nextMode);
      syncCameraHudMode();
    },
  }));
  inputSystem.init();
  const { input, touchActive } = inputSystem;

  services.register(AudioSystem, new AudioSystem()).init();
  const assetSystem = services.register(AssetSystem, new AssetSystem());

  const setLeaderboardInspectFromKey = (ev: KeyboardEvent, active: boolean): void => {
    if (ev.code !== 'KeyL' || isKeyboardTextTarget(ev.target)) return;
    if (mode !== 'racing') {
      hud.setLeaderboardInspect(false);
      return;
    }
    ev.preventDefault();
    hud.setLeaderboardInspect(active);
  };
  window.addEventListener('keydown', (ev) => setLeaderboardInspectFromKey(ev, true));
  window.addEventListener('keyup', (ev) => setLeaderboardInspectFromKey(ev, false));

  // Backtick toggles the perf HUD so it can be hidden for screenshots / demos
  // without losing the always-on dev gating. Only wires up when the HUD is
  // mounted in the first place.
  if (perfHud) {
    window.addEventListener('keydown', (ev) => {
      if (ev.code !== 'Backquote' || isKeyboardTextTarget(ev.target)) return;
      if (ev.metaKey || ev.ctrlKey || ev.altKey) return;
      ev.preventDefault();
      perfHud.toggleVisible();
    });
  }

  if (touchActive) {
    setupTouchPauseButton(() => {
      if (mode === 'racing') t.toPaused();
      else if (mode === 'paused') t.fromPaused();
    });
  }

  // Android long-press fires `contextmenu` on the canvas (and HUD) which
  // pops up the OS share sheet over the race. iOS callouts are killed via
  // CSS; this catches the Android side. Inputs in modals are unaffected.
  for (const el of [appEl, document.getElementById('hud'), document.getElementById('touch-controls'), document.getElementById('touch-sticks')]) {
    el?.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  // iOS-only WebKit gesture events fire on two-finger pinch/rotate. We
  // already set `touch-action: none` on the canvas and controls, but the
  // gesture events bypass that and can scrub the OS into a zoom that
  // visually breaks the HUD mid-race. Cancel them at the window level so
  // no element along the path can swallow them as a passive listener.
  for (const evt of ['gesturestart', 'gesturechange', 'gestureend']) {
    window.addEventListener(evt, (e) => e.preventDefault(), { passive: false });
  }

  // Keep the screen on during a race (best-effort; many WebViews refuse).
  // The sentinel auto-releases when the tab hides; ScreenWakeLock re-acquires
  // on the next visibilitychange.
  const wakeLock = new ScreenWakeLock();
  wakeLock.attach();

  // Auto-pause if the tab goes to the background (incoming call, app switch).
  // Without this the engine synth and physics keep running silently while
  // the player is elsewhere — and resume jarringly when they come back.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && mode === 'racing') {
      t.toPaused();
    }
  });

  const onResize = (): void => {
    const w = appEl.clientWidth;
    const h = appEl.clientHeight;
    renderer.setSize(w, h, false);
    bloom?.setSize(w, h);
    followCam.setAspect(w / h);
  };
  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onResize);
  // iOS Safari hides/shows its toolbar mid-session and only fires resize on
  // visualViewport for that change, not on window. Without this listener the
  // canvas keeps the pre-toolbar aspect and reads as a "stretched" render.
  if (typeof window.visualViewport !== 'undefined' && window.visualViewport) {
    window.visualViewport.addEventListener('resize', onResize);
  }
  onResize();

  // Dynamic resolution scaling. Production-like play uses the same auto DRS
  // path on Vite (?prod / ?production / ?playtest) and on real builds. Plain
  // dev boots fixed at the renderer's resolved DPR (normally 1.00, unless a
  // URL ?dpr override is present) so GAME and MAP do not silently diverge
  // while tuning; the Game dev panel can still force preview scales manually
  // or opt into Auto DRS.
  const drsParam = (params.get('drs') ?? '').trim().toLowerCase();
  const drsModeDefault = rendererHandle.isWebGPU ? 'scene-resolution' : 'setsize';
  const drsModeParam = (params.get('drsMode') ?? drsModeDefault).trim().toLowerCase();
  const drsRequestedMode = drsModeParam === 'scene-resolution' || drsModeParam === 'subrect'
    ? 'scene-resolution'
    : 'setsize';
  const drsApplyMode = drsRequestedMode === 'scene-resolution' && rendererHandle.isWebGPU && bloom
    ? 'scene-resolution'
    : 'setsize';
  if (drsModeParam === 'subrect') {
    console.info('[drs] drsMode=subrect is reserved for true viewport-subrect DRS; using scene-resolution mode for the WebGPU scene pass');
  }
  if (drsRequestedMode === 'scene-resolution' && !rendererHandle.isWebGPU) {
    console.info('[drs] scene-resolution mode is WebGPU-only; using setSize mode');
  } else if (drsRequestedMode === 'scene-resolution' && !bloom) {
    console.info('[drs] scene-resolution mode requires the WebGPU post-FX pipeline; using setSize mode');
  } else if (drsApplyMode === 'scene-resolution') {
    console.info('[drs] using WebGPU scene-pass resolution scaling; true viewport-subrect upscale remains deferred');
  }
  const drsExplicitlyOff = drsParam === 'off' || drsParam === 'false' || drsParam === '0' || drsParam === 'fixed';
  const drsExplicitlyOn = params.has('drs') && !drsExplicitlyOff;
  const drsAutoDefault = runtime.productionLike || !import.meta.env.DEV;
  const drsPersistenceEnabled = runtime.productionLike || !import.meta.env.DEV;
  const nativeDpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  const drsMaxScale = Math.max(DEV_FIXED_RENDER_SCALE, Math.min(nativeDpr, 1.5));
  const bootRenderScale = renderer.getPixelRatio();
  const renderScaleOverride = parseRenderScaleOverride(params.get('renderScale'));
  const drsAutoEnabled = renderScaleOverride === null && !drsExplicitlyOff && (drsAutoDefault || drsExplicitlyOn);
  const initialFixedRenderScale = renderScaleOverride ?? (Number.isFinite(bootRenderScale) && bootRenderScale > 0
    ? bootRenderScale
    : DEV_FIXED_RENDER_SCALE);
  const renderScaleMax = Math.max(DEV_FIXED_RENDER_SCALE, drsMaxScale, initialFixedRenderScale);
  const renderScaleMin = Math.min(RENDER_SCALE_MIN_PREVIEW, initialFixedRenderScale);
  const drsSystem = services.register(DrsSystem, new DrsSystem({
    initialFixedRenderScale,
    renderScaleMin,
    renderScaleMax,
    nativeDpr,
    drsMaxScale,
    drsExplicitlyOff,
    drsPersistenceEnabled,
    drsAutoEnabled,
    targetMs: 1000 / 60,
  }));
  const drs = drsSystem.drs;
  const applyDrsTargetBudget = (): void => {
    if (!drs) return;
    const displayMs = drsSystem.detectedRefreshMs ?? 1000 / 60;
    const capMs = Number.isFinite(activeRenderHzCap) ? 1000 / activeRenderHzCap : 0;
    const targetMs = Math.max(displayMs, capMs);
    drsSystem.setTargetMs(targetMs);
    perfHud?.setTargetHz(1000 / targetMs);
  };
  const renderScaleTuning = {
    get mode(): DrsRenderScaleMode { return drsSystem.mode; },
    preset: '1.00 fixed',
    get scale(): number { return drsSystem.manualScale; },
    get current(): number { return drsApplyMode === 'scene-resolution' ? drsSystem.current() : renderer.getPixelRatio(); },
    nativeDpr,
    minScale: renderScaleMin,
    maxScale: renderScaleMax,
    autoAvailable: drsSystem.autoAvailable,
    get autoActive(): boolean { return drsSystem.mode === 'auto'; },
    productionLike: runtime.productionLike || !import.meta.env.DEV,
    presets: RENDER_SCALE_PRESETS,
    setMode(mode: DrsRenderScaleMode): void {
      const wasAuto = drsSystem.mode === 'auto';
      drsSystem.setMode(mode);
      if (drsSystem.mode === 'auto' && !wasAuto) applyDrsTargetBudget();
    },
    setScale(scale: number): void {
      drsSystem.setScale(scale);
    },
    setPreset(preset: string): void {
      const scale = RENDER_SCALE_PRESETS[preset];
      if (!Number.isFinite(scale)) return;
      this.preset = preset;
      this.setScale(scale);
    },
    reset(): void {
      this.setPreset('1.00 fixed');
    },
  };
  const applyRenderScale = (scale: number): void => {
    const nextScale = drsSystem.clampScale(scale);
    if (drsApplyMode === 'scene-resolution') {
      if (renderer.getPixelRatio() !== renderScaleMax) {
        renderer.setPixelRatio(renderScaleMax);
        postFx.setPixelRatio(renderScaleMax);
        onResize();
      }
      postFx.setRenderScale(THREE.MathUtils.clamp(nextScale / Math.max(0.0001, renderScaleMax), 0.25, 1));
      perfHud?.setEffectiveScale(nextScale);
      return;
    }
    postFx.setRenderScale(1);
    renderer.setPixelRatio(nextScale);
    // EffectComposer caches its pixel ratio at construction — without this
    // its render targets stay at the boot DPR even after the renderer
    // updates, and bloom blits at the wrong resolution.
    postFx.setPixelRatio(nextScale);
    onResize();
    perfHud?.setEffectiveScale(nextScale);
  };
  drsSystem.setApplyScale(applyRenderScale);
  // Sync renderer to DRS's starting scale so the very first frame matches
  // what DRS thinks is current. Without this, DRS would never scale up
  // from `current` because the renderer is already there in its view, but
  // visually it'd be stuck at the resolvePixelRatio cap.
  if (drsSystem.mode === 'auto' && drs) {
    applyDrsTargetBudget();
    applyRenderScale(drs.current());
  } else {
    applyRenderScale(drsSystem.manualScale);
  }

  const RECORD_CACHE_KEY = `tst:record:${TRACK_ID}`;
  type CachedRecord = { totalMs: number; holder: string; deviceId: string };

  const readCachedRecord = (): CachedRecord | null => {
    try {
      const raw = localStorage.getItem(RECORD_CACHE_KEY);
      if (!raw) return null;
      const d = JSON.parse(raw);
      if (
        d && typeof d === 'object'
        && typeof d.totalMs === 'number'
        && typeof d.holder === 'string'
        && typeof d.deviceId === 'string'
      ) return d as CachedRecord;
    } catch { /* ignore */ }
    return null;
  };

  const writeCachedRecord = (r: CachedRecord): void => {
    try { localStorage.setItem(RECORD_CACHE_KEY, JSON.stringify(r)); } catch { /* ignore */ }
  };

  const applyRecordToHud = (r: CachedRecord | null): void => {
    if (!r) { hud.setRecord(null, null, false); return; }
    const myDevice = identity?.deviceId ?? '';
    hud.setRecord(r.totalMs, r.holder, myDevice.length > 0 && r.deviceId === myDevice);
  };

  let localPbSyncAttemptMs: number | null = null;
  const readLocalBestSectors = (): [number, number, number] | null => {
    const sectors = lapTimer.state.bestSectors;
    if (sectors.some((sector) => sector === null || !Number.isFinite(sector))) return null;
    return [sectors[0], sectors[1], sectors[2]] as [number, number, number];
  };

  const matchingLocalPbGhost = (totalMs: number): GhostRecording | null => {
    return personalGhost && Math.abs(personalGhost.lapMs - totalMs) <= 2
      ? personalGhost
      : null;
  };

  const makeOptimisticLocalPersonalBest = (id: Identity | null): LeaderboardEntry | null => {
    const localBest = lapTimer.state.bestLap;
    const sectors = readLocalBestSectors();
    if (localBest === null || !Number.isFinite(localBest) || !sectors) return null;
    const totalMs = Math.max(1, Math.round(localBest));
    const [s1, s2, s3] = sectors.map((sector) => Math.max(1, Math.round(sector))) as [number, number, number];
    return {
      id: `local-pb:${id?.deviceId ?? 'pending'}`,
      rank: 0,
      handle: id?.handle ?? 'You',
      homeworld: id?.homeworld ?? null,
      total_ms: totalMs,
      sector1_ms: s1,
      sector2_ms: s2,
      sector3_ms: s3,
      device_id: id?.deviceId ?? 'local-pending',
      user_id: id?.userId ?? null,
      created_at: 'local',
      has_ghost: matchingLocalPbGhost(totalMs) !== null,
    };
  };

  const rankLeaderboardRows = (rows: LeaderboardEntry[]): LeaderboardEntry[] => {
    let previousTime: number | null = null;
    let previousRank = 0;
    return rows.map((row, index) => {
      const rank = row.total_ms === previousTime ? previousRank : index + 1;
      previousTime = row.total_ms;
      previousRank = rank;
      return { ...row, rank };
    });
  };

  const mergeOptimisticLocalPersonalBest = (
    rows: LeaderboardEntry[],
    id: Identity | null,
  ): LeaderboardEntry[] => {
    const local = makeOptimisticLocalPersonalBest(id);
    if (!local || !id) return rankLeaderboardRows(rows);
    const serverMine = rows.find((row) => isOwnLeaderboardRow(row, id)) ?? null;
    if (serverMine && serverMine.total_ms <= local.total_ms) {
      return rankLeaderboardRows(rows);
    }
    const merged = rows
      .filter((row) => !isOwnLeaderboardRow(row, id))
      .concat(local)
      .sort((a, b) => a.total_ms - b.total_ms || a.id.localeCompare(b.id));
    return rankLeaderboardRows(merged);
  };

  const syncLocalPersonalBestIfAhead = async (serverMine: LeaderboardEntry | null): Promise<void> => {
    if (devMode || !isBackendConfigured()) return;
    const localBest = lapTimer.state.bestLap;
    if (localBest === null || !Number.isFinite(localBest)) return;
    const localTotalMs = Math.max(1, Math.round(localBest));
    if (serverMine && serverMine.total_ms <= localTotalMs) return;
    const sectors = readLocalBestSectors();
    if (!sectors) return;
    if (localPbSyncAttemptMs === localTotalMs) return;
    localPbSyncAttemptMs = localTotalMs;
    await identityPromise;
    const result = await submitLap({
      totalMs: localTotalMs,
      sectorMs: sectors,
      trackId: TRACK_ID,
      ghost: matchingLocalPbGhost(localTotalMs),
    });
    if (result.ok) void refreshTimingBoard();
    else console.warn('[twin-suns-turbo] local PB sync failed', result.error);
  };

  let serverTimingRows: LeaderboardEntry[] = [];

  const applyTimingBoardRows = async (
    serverRows: LeaderboardEntry[],
    options: { syncLocalPb?: boolean; fetchOwnFallback?: boolean; fetchRecordFallback?: boolean } = {},
  ): Promise<void> => {
    const id = getIdentity();
    const top = mergeOptimisticLocalPersonalBest(serverRows, id);
    if (top.length === 0) {
      if (options.syncLocalPb && id) void syncLocalPersonalBestIfAhead(null);
      void syncLeaderboardGhostTarget(null, id, []);
      if (options.fetchRecordFallback) {
        const record = await fetchLeaderboard('alltime', TRACK_ID, 1);
        if (record.length > 0) {
          const r: CachedRecord = {
            totalMs: record[0].total_ms,
            holder: record[0].handle,
            deviceId: record[0].device_id,
          };
          writeCachedRecord(r);
          applyRecordToHud(r);
        }
      }
      hud.setLeaderboardRows([]);
      return;
    }

    const r: CachedRecord = {
      totalMs: top[0].total_ms,
      holder: top[0].handle,
      deviceId: top[0].device_id,
    };
    writeCachedRecord(r);
    applyRecordToHud(r);

    if (!id) {
      void syncLeaderboardGhostTarget(top[0] ?? null, null, top, {
        personalBestFallbackTitle: 'Personal Ghost',
      });
      hud.setLeaderboardRows(buildHudLeaderboardRows(top));
      return;
    }

    const myIndex = top.findIndex((row) => isOwnLeaderboardRow(row, id));
    if (myIndex < 0) {
      const mine = options.fetchOwnFallback ? await fetchOwnPersonalBest('alltime', TRACK_ID) : null;
      if (mine) {
        if (options.syncLocalPb) void syncLocalPersonalBestIfAhead(mine);
        void syncLeaderboardGhostTarget(top[0] ?? mine, id, top, {
          personalBestFallbackTitle: 'Personal Ghost',
        });
        hud.setLeaderboardRows(buildHudLeaderboardRows(top, [null, mine, null]));
        return;
      }
      if (options.syncLocalPb) void syncLocalPersonalBestIfAhead(null);
      void syncLeaderboardGhostTarget(top[0] ?? null, id, top, {
        personalBestFallbackTitle: 'Personal Ghost',
      });
      hud.setLeaderboardRows(buildHudLeaderboardRows(top));
      return;
    }

    const mine = top[myIndex];
    if (options.syncLocalPb) void syncLocalPersonalBestIfAhead(mine);
    void syncLeaderboardGhostTarget(myIndex > 0 ? top[myIndex - 1] : mine, id, top, {
      includeSlowerFallback: false,
      personalBestFallbackTitle: myIndex === 0 ? 'Target Ghost' : 'Personal Ghost',
    });
    const nearby = [
      myIndex > 0 ? top[myIndex - 1] : null,
      mine,
      myIndex < top.length - 1 ? top[myIndex + 1] : null,
    ];
    hud.setLeaderboardRows(buildHudLeaderboardRows(top, nearby));
  };

  const refreshTimingBoard = async (): Promise<void> => {
    if (!isBackendConfigured()) return;
    try {
      const top = await fetchLeaderboard('alltime', TRACK_ID, 100, 'personalBest');
      serverTimingRows = top;
      await applyTimingBoardRows(top, {
        syncLocalPb: true,
        fetchOwnFallback: true,
        fetchRecordFallback: true,
      });
    } catch { /* ignore — keep whatever was cached */ }
  };

  let lastHudTimingScope = currentStorageScope();
  syncPilotScopedTiming = (): void => {
    const scope = currentStorageScope();
    if (scope !== lastHudTimingScope) {
      hud.setLastLap(null);
      lastHudTimingScope = scope;
    }
    lapTimer.setStorageScope(scope);
    setPersonalGhost(loadGhost(TRACK_ID, PHYSICS_VERSION, null, scope));
    if (mode === 'racing' && lapTimer.state.running) return;

    const matchingPersonalGhost = matchingLocalPersonalGhost();
    if (matchingPersonalGhost) {
      setDesiredGhost(matchingPersonalGhost, 'Personal Best', 'Personal Ghost');
    } else if (localBestMs() === null) {
      setTrainingGhostTarget();
    } else {
      setDesiredGhost(null, '', 'Target Ghost');
    }
  };

  // Show whatever's in localStorage immediately so the row isn't blank on
  // the first race; the network refresh happens below in parallel.
  applyRecordToHud(readCachedRecord());
  hud.setLeaderboardRows([]);
  void refreshTimingBoard();
  void identityPromise.then(() => refreshTimingBoard());

  const handleLapClosed = async (
    total: number,
    sectors: [number, number, number],
    ghost: GhostRecording | null,
  ): Promise<void> => {
    // Every eligible lap goes up — not just personal bests — so the backend
    // has the full dataset. The HUD flashes the new-best in place so the
    // player can keep racing.
    await identityPromise;
    const outcome = await submitLapIfEligible({
      devMode,
      raceMode: currentRaceMode,
      backendConfigured: isBackendConfigured(),
      trackId: TRACK_ID,
      totalMs: total,
      sectors,
      ghost,
    });
    if (!outcome.submitted) return;
    if (outcome.result.ok) {
      void refreshTimingBoard();
    } else {
      console.warn('[twin-suns-turbo] lap submission failed', outcome.result.error);
    }
  };

  const captureGhostStartState = (): GhostStartState => ({
    x: racer.state.position.x,
    y: racer.state.position.y,
    z: racer.state.position.z,
    heading: racer.state.heading,
    velocityX: racer.state.velocity.x,
    velocityZ: racer.state.velocity.z,
  });

  const resetLiveLapProgress = (): void => {
    const arc = locateTrackPosition(track, racer.state.position.x, racer.state.position.z).arcLength;
    liveLapProgress = 0;
    prevLiveLapArc = arc;
  };

  const advanceLiveLapProgress = (): void => {
    const arc = locateTrackPosition(track, racer.state.position.x, racer.state.position.z).arcLength;
    if (prevLiveLapArc === null) {
      liveLapProgress = 0;
      prevLiveLapArc = arc;
      return;
    }
    let delta = arc - prevLiveLapArc;
    if (delta < -track.totalLength * 0.5) delta += track.totalLength;
    else if (delta > track.totalLength * 0.5) delta -= track.totalLength;
    liveLapProgress = Math.max(0, Math.min(track.totalLength, (liveLapProgress ?? 0) + delta));
    prevLiveLapArc = arc;
  };

  const beginTiming = (): boolean => {
    if (lapTimer.state.running) return false;
    lapTimer.start(performance.now());
    liveLapFixedMs = 0;
    resetLiveLapProgress();
    cpTracker.reset();
    ghostRecorder.markLapStart(captureGhostStartState());
    if (autoRaceActive) startAutopilotLap(autopilotState, raceSystem.stepCount);
    ghostShip.resetReplay();
    ghostReplayActive = !opponentRaceEnabled && activeGhost !== null;
    ghostShip.setVisible(ghostReplayActive);
    return true;
  };

  const resetRunState = (): void => {
    input.reset();
    inputSystem.resetTouch();
    applyPlayerGridStart();
    applyAutoRaceSpawnOverride();
    respawnState = 'live';
    respawnTimer = 0;
    respawnFade.style.opacity = '0';
    lastSafeRespawn.x = racer.state.position.x;
    lastSafeRespawn.z = racer.state.position.z;
    lastSafeRespawn.heading = racer.state.heading;
    prevSurfaceY = null;
    racer.state.verticalVelocity = 0;
    racer.state.airborne = false;
    syncRacerGroundHeight(0);
    racer.render(1);
    followCam.resetTo(racer.state.position, racer.state.heading);
    cpTracker.reset();
    lapTimer.reset(performance.now());
    prevStartGateSign = -1;
    hud.notifyReset();
    wrongWay.reset();
    offTrackBudget.reset();
    hud.setWrongWay(false);
    dust.clear();
    engines.clear();
    scrapes.clear();
    ghostRecorder.start(FIXED_UPDATE_DT);
    applyDesiredGhost();
    ghostShip.resetReplay();
    ghostReplayActive = false;
    liveLapFixedMs = null;
    liveLapProgress = null;
    prevLiveLapArc = null;
    latestGhostDeltaMs = null;
    ghostShip.setVisible(false);
    hud.setGhostTracker(null);
    hud.setLivePosition(null);
    if (opponentRaceEnabled) {
      hud.setTargetSplits(null, 'Rival Sectors');
      hud.setRaceTarget({ label: 'Rival', totalMs: null, note: 'Computer opponent' });
    }
    wallContactCache.active = false;
    wallContactCache.seconds = 0;
    collisionDebug.active = false;
    collisionDebug.source = 'none';
    resetAutopilotRun(autopilotState);
    if (opponentController && opponentRacer) {
      opponentResetCount++;
      resetOpponentController(track, opponentController, performance.now());
      placeOpponentOnGrid();
    }
    clearRaceCountdown();
  };

  let latestGroundY = 0;
  let latestKerbY = 0;
  let prevSurfaceY: number | null = null;
  // Reused per-frame in the god-ray pass to project the sun direction into
  // the camera's NDC space. Hoisted so the hot path doesn't allocate.
  const godRaySunWorld = new THREE.Vector3();
  const godRaySunNDC = new THREE.Vector3();
  /**
   * Airborne-aware ground sync. On the ground, `state.position.y` snaps
   * to the track surface (legacy behavior). When the racer crosses a lip
   * where the surface drops away faster than gravity can follow, we go
   * `airborne` — gravity integrates `state.verticalVelocity` and the
   * chassis arcs through the air until it re-contacts the surface, at
   * which point we apply a landing impulse to the suspension spring.
   *
   * The takeoff trigger compares the surface velocity we were carrying
   * (rate of ground rise/fall under us last frame) against the rate it
   * would need to fall this frame to lose contact under gravity. If the
   * surface drops faster than the racer can be glued down, we fly.
   */
  const syncRacerGroundHeight = (dt: number): void => {
    const x = racer.state.position.x;
    const z = racer.state.position.z;
    const groundY = trackSurfaceHeightAtPosition(track, x, z);
    const kerbY = trackMeshes.kerbSurface.heightAt(x, z);
    latestGroundY = groundY;
    latestKerbY = kerbY;
    const surfaceY = groundY + (perfToggles.kerbY ? kerbY : 0);

    if (racer.state.airborne) {
      racer.state.verticalVelocity -= racer.gravityY * dt;
      racer.state.position.y += racer.state.verticalVelocity * dt;
      if (racer.state.position.y <= surfaceY) {
        const impactSpeed = -racer.state.verticalVelocity;
        racer.state.position.y = surfaceY;
        racer.state.verticalVelocity = 0;
        racer.state.airborne = false;
        if (impactSpeed > 0) racer.applyLandingImpulse(impactSpeed);
      }
      prevSurfaceY = surfaceY;
      return;
    }

    // Grounded path: detect takeoff. If the previous frame's surface was
    // rising or level and this frame's surface drops abruptly (cliff,
    // ramp lip), launch with the rising velocity we were carrying.
    if (prevSurfaceY !== null && dt > 0) {
      const surfaceVel = (surfaceY - prevSurfaceY) / dt;
      // Carrying upward velocity at the lip: the bigger the slope we just
      // came off, the bigger the launch. Even a flat → drop-off launches
      // at zero (gravity just pulls us down). To detect drop-offs cleanly:
      // require the next sample to be lower than where we'd be next frame
      // if we kept our current vertical velocity and gravity acted.
      const wouldBeY = racer.state.position.y + surfaceVel * dt - 0.5 * racer.gravityY * dt * dt;
      if (wouldBeY > surfaceY + 0.005) {
        racer.state.airborne = true;
        racer.state.verticalVelocity = Math.max(0, surfaceVel);
        racer.state.position.y = wouldBeY;
        prevSurfaceY = surfaceY;
        return;
      }
    }
    racer.state.position.y = surfaceY;
    prevSurfaceY = surfaceY;
  };

  const triggerRespawn = (): void => {
    respawnState = 'dying';
    respawnTimer = 0;
    racer.state.velocity.set(0, 0, 0);
    racer.state.yawVelocity = 0;
  };

  const perfLab = perfLabEnabled
    ? new PerfLab({
      renderer,
      scene,
      gpuTimer,
      getRuntime: () => ({
        mode,
        reducedRenderLoad,
        reducedRenderLoadAt,
        reducedRenderLoadReason,
        renderedFrameCount: loop.renderedFrameCount,
        renderSkipCount: loop.renderSkipCount,
        stepCount: raceSystem.stepCount,
        renderHzCap: Number.isFinite(diagRenderHzOverride ?? activeRenderHzCap) ? (diagRenderHzOverride ?? activeRenderHzCap) : 'uncapped',
        autoFrameCap,
        terrainShaderMode,
        asphaltShaderMode,
        drs: drsSystem.snapshot().drs,
      }),
    })
    : null;
  const perfLabPanel = perfLab ? new PerfLabPanel(perfLab) : null;
  const SHADOW_MAP_REFRESH_MAX_FRAMES = 12;
  const SHADOW_MAP_REFRESH_DISTANCE_SQ = 8 * 8;
  const shadowMapFocus = new THREE.Vector3();
  const lastShadowMapFocus = new THREE.Vector3(Number.NaN, Number.NaN, Number.NaN);
  let shadowMapRefreshFrames = SHADOW_MAP_REFRESH_MAX_FRAMES;
  let loop: GameLoop;
  const setActiveRenderHzCap = (hz: number, reason: string): void => {
    const next = Number.isFinite(hz) ? Math.max(1, hz) : Number.POSITIVE_INFINITY;
    if (next === activeRenderHzCap) return;
    activeRenderHzCap = next;
    loop.setMaxRenderHz(diagRenderHzOverride ?? next);
    applyDrsTargetBudget();
    perf.event('renderHzCapChanged', {
      hz: Number.isFinite(next) ? next : 'uncapped',
      reason,
      tMs: performance.now(),
    });
  };
  const setDiagRenderHzOverride = (hz: number | null, reason: string): void => {
    diagRenderHzOverride = hz === null
      ? null
      : (Number.isFinite(hz) ? Math.max(1, hz) : Number.POSITIVE_INFINITY);
    const effective = diagRenderHzOverride ?? activeRenderHzCap;
    loop.setMaxRenderHz(effective);
    applyDrsTargetBudget();
    perf.event('renderHzCapChanged', {
      hz: Number.isFinite(effective) ? effective : 'uncapped',
      reason,
      tMs: performance.now(),
    });
  };
  loop = new GameLoop({
    fixedHz: FIXED_UPDATE_HZ,
    maxSubsteps: MAX_GAMEPLAY_CATCHUP_STEPS,
    slowFrameThresholdSteps: SLOW_FRAME_THRESHOLD_STEPS,
    pausedRenderHz: 8,
    maxRenderHz: activeRenderHzCap,
    onRaf: (now) => perfHud?.tickRaf(now),
    update: (dt) => {
      // Drive the stick snap-to-zero spring before reading raw axes so
      // the released-thrust easing is visible in the same frame.
      inputSystem.tickFixed();

      // Respawn freeze: zero input/physics while the screen fades.
      if (respawnState === 'dying') {
        respawnTimer += dt;
        respawnFade.style.opacity = String(Math.min(1, respawnTimer / RESPAWN_FADE_S));
        racer.state.velocity.set(0, 0, 0);
        if (respawnTimer >= RESPAWN_FADE_S) {
          racer.setStart(lastSafeRespawn.x, lastSafeRespawn.z, lastSafeRespawn.heading);
          racer.state.position.y = trackSurfaceHeightAtPosition(track, racer.state.position.x, racer.state.position.z)
            + trackMeshes.kerbSurface.heightAt(racer.state.position.x, racer.state.position.z);
          racer.state.verticalVelocity = 0;
          racer.state.airborne = false;
          prevSurfaceY = null;
          racer.render(1);
          followCam.resetTo(racer.state.position, racer.state.heading);
          respawnState = 'invuln';
          respawnTimer = 0;
        }
        return;
      }
      if (respawnState === 'invuln') {
        respawnTimer += dt;
        const fadeBack = Math.max(0, 1 - respawnTimer / RESPAWN_FADE_S);
        respawnFade.style.opacity = String(fadeBack);
        if (respawnTimer >= RESPAWN_INVULN_S) {
          respawnState = 'live';
          respawnFade.style.opacity = '0';
        }
      }
      if (mode === 'racing' && raceCountdownActive()) {
        perf.measure('input', () => input.update(dt));
        const heldInput = input.getState();
        stepRaceCountdown(dt);
        const pose = playerGridPose();
        const groundY = trackSurfaceHeightAtPosition(track, pose.x, pose.z)
          + trackMeshes.kerbSurface.heightAt(pose.x, pose.z);
        pinRacerStateForStartHold(racer.state, pose, groundY);
        racer.beginStep();
        pinOpponentOnGrid();
        racer.setEngineThrust(heldInput.leftThrust, heldInput.rightThrust);
        const heldThrottle = startHoldThrottleSignal(heldInput);
        racer.setPodRacerPreviewThrottle(heldThrottle);
        perf.measure('audio', () => {
          gameAudio.update({
            throttle: heldThrottle,
            brake: heldInput.brake,
            speed: heldThrottle * 0.25,
            slip: 0,
            load: heldThrottle,
            onTrack: true,
            boost: 0,
            boostCharge: racer.state.driftCharge,
            airBrake: heldInput.brake,
          }, dt);
        });
        syncRacerGroundHeight(0);
        return;
      }

      perf.measure('input', () => input.update(dt));
      if (mode === 'racing') stepOpponentRuntime(dt);
      let rawInputState = input.getState();
      if (autoRaceActive) {
        perf.measure('autopilot', () => {
          rawInputState = computeAutopilotInput(track, racer.state, autopilotState, {
            step: raceSystem.stepCount,
            dt,
          });
        });
      }
      racer.beginStep();
      racer.setEngineThrust(rawInputState.leftThrust, rawInputState.rightThrust);
      const stepStartX = racer.state.position.x;
      const stepStartZ = racer.state.position.z;

      const onTrack = isOnTrack(track, racer.state.position.x, racer.state.position.z);
      const onKerb = trackMeshes.kerbSurface.isOnKerb(
        racer.state.position.x,
        racer.state.position.z,
      );
      const onRacingSurface = onTrack || onKerb;
      racer.surfaceRoughness = onRacingSurface
        ? racer.trackSurfaceRoughness
        : racer.offTrackSurfaceRoughness;
      const inputState = rawInputState;
      const throttleSignal = startHoldThrottleSignal(inputState);
      let startedTimingThisStep = false;
      racer.setPodRacerPreviewThrottle(throttleSignal);
      perf.measure('physics', () => {
        physicsStep(racer.state, inputState, physics, onRacingSurface, dt);
        racer.applyCockpitWhipPhysics(dt);
      });
      const stepSweep = {
        fromX: stepStartX,
        fromZ: stepStartZ,
        toX: racer.state.position.x,
        toZ: racer.state.position.z,
      };
      const wallPerpDamping = 1 - THREE.MathUtils.clamp(physics.collisionRestitution, 0, 0.85);
      perf.measure('groundSync', () => syncRacerGroundHeight(dt));

      // Anticipation tell: in the 25 ms before a corridor hit lands, fire a
      // small FOV pull + rumble preload. The brain registers the lead-in
      // even though the player can't react in 16 ms, and the felt
      // difference between "felt the impact coming" and "the camera lurched
      // out of nowhere" is significant. Cheap — one corridor lookup.
      const _lookaheadDt = 0.025;
      const _predX = racer.state.position.x + racer.state.velocity.x * _lookaheadDt;
      const _predZ = racer.state.position.z + racer.state.velocity.z * _lookaheadDt;
      const _predTrack = locateTrackPosition(track, _predX, _predZ);
      const _predLateralMag = Math.abs(_predTrack.signedLateral);
      const _predEdge = _predTrack.signedLateral > 0
        ? _predTrack.widthLeft + track.wallBuffer
        : _predTrack.widthRight + track.wallBuffer;
      const _willHitWall = _predLateralMag > _predEdge - 0.5
        && !contactState.active
        && Math.hypot(racer.state.velocity.x, racer.state.velocity.z) > 25;
      if (_willHitWall) {
        followCam.fovPunch(1.0);
      }

      let terrainHit: WallHit = NO_HIT;
      let scrapeHit: SoftBarrierHit = NO_SOFT_HIT;
      let wallObstacleHit: WallHit = NO_HIT;
      let obstacleHit: WallHit = NO_HIT;
      let meshHit: WallHit = NO_HIT;
      let corridorHit: WallHit = { hit: false, impactSpeed: 0, nx: 0, nz: 0 };
      let dominantHit: WallHit = { hit: false, impactSpeed: 0, nx: 0, nz: 0 };
      let wallContact: WallHit = { hit: false, impactSpeed: 0, nx: 0, nz: 0 };
      // Snapshot pre-collision velocity for contact-state impulse calc.
      _preCollisionVelocity.x = racer.state.velocity.x;
      _preCollisionVelocity.z = racer.state.velocity.z;
      perf.measure('collision', () => {
        let activeSweep: MovementSegment2D | undefined = stepSweep;
        const clearSweepIfResolved = (hit: WallHit): void => {
          if (hit.hit) activeSweep = undefined;
        };
        terrainHit = !onRacingSurface
          ? resolveTerrainSlopeCollision(
              racer.state.position,
              racer.state.velocity,
              terrain.sampleHeight,
              MAX_TERRAIN_CLIMB_RAD,
            )
          : NO_HIT;
        // Visible barriers FIRST (rails, fences, wall blocks) — they catch the
        // racer at the same XZ position the player can see. The corridor wall
        // and OBBs come after as safety nets so the racer never escapes.
        scrapeHit = trackFurniture.collisionLines.length > 0
          ? resolveSoftScrapeCollision(
              racer.state.position,
              racer.state.velocity,
              trackFurniture.collisionLines,
              RACER_RAIL_SKIN,
              dt,
              activeSweep,
            )
          : NO_SOFT_HIT;
        if (scrapeHit.hit && !scrapeHit.scraping) activeSweep = undefined;
        wallObstacleHit = trackFurniture.obstacles.length > 0
          ? resolveObstacleCollision(
              racer.state.position,
              racer.state.velocity,
              trackFurniture.obstacles,
              RACER_RAIL_SKIN,
              wallPerpDamping,
              activeSweep,
            )
          : NO_HIT;
        clearSweepIfResolved(wallObstacleHit);
        obstacleHit = sceneryObstacles.length > 0
          ? resolveObstacleCollision(
              racer.state.position,
              racer.state.velocity,
              sceneryObstacles,
              RACER_COLLISION_CLEARANCE,
              wallPerpDamping,
              activeSweep,
            )
          : NO_HIT;
        clearSweepIfResolved(obstacleHit);
        meshHit = sceneryMeshColliders.length > 0
          ? resolveMeshCollision(
              racer.state.position,
              racer.state.velocity,
              sceneryMeshColliders,
              RACER_RAIL_SKIN,
              wallPerpDamping,
            )
          : NO_HIT;
        clearSweepIfResolved(meshHit);
        corridorHit = resolveTrackCorridorCollision(
          racer.state.position,
          racer.state.velocity,
          track,
          wallPerpDamping,
          RACER_COLLISION_CLEARANCE,
          activeSweep,
        );
        dominantHit = pickDominantHit(
          terrainHit,
          corridorHit,
          scrapeHit,
          obstacleHit,
          wallObstacleHit,
          meshHit,
        );
        wallContact = pickWallContact(
          racer.state.velocity,
          corridorHit,
          scrapeHit,
          obstacleHit,
          wallObstacleHit,
          meshHit,
        );
      });
      const contactWasSame = wallContact.hit
        && wallContactCache.active
        && sameWallNormal(wallContactCache, wallContact, physics.collisionSameNormalDot);
      const wallContactSeconds = wallContact.hit
        ? contactWasSame ? wallContactCache.seconds + dt : 0
        : 0;
      if (wallContact.hit) {
        applyWallContactResponse(racer.state.velocity, wallContact, dt, physics, wallContactSeconds);
        wallContactCache.active = true;
        wallContactCache.nx = wallContact.nx;
        wallContactCache.nz = wallContact.nz;
        wallContactCache.seconds = wallContactSeconds;
      } else {
        wallContactCache.active = false;
        wallContactCache.seconds = 0;
      }
      // Update the unified contact state from the resolved hit. Pre-/post-
      // velocity bracket the response so impactImpulse measures actual Δv,
      // not just the resolver's reported impactSpeed (which is pre-response).
      // Contact point is offset along the inward normal by the racer's
      // collision radius so sparks emit ON the wall surface, not floating
      // next to the cockpit.
      const _contactNewTransition = wallContact.hit && !contactWasSame;
      const _contactReach = RACER_COLLISION_CLEARANCE * 0.92;
      updateContactState(contactState, {
        hit: wallContact,
        preVelocityX: _preCollisionVelocity.x,
        preVelocityZ: _preCollisionVelocity.z,
        postVelocityX: racer.state.velocity.x,
        postVelocityZ: racer.state.velocity.z,
        contactX: racer.state.position.x + wallContact.nx * _contactReach,
        contactY: racer.state.position.y + 0.3,
        contactZ: racer.state.position.z + wallContact.nz * _contactReach,
        contactSeconds: wallContactSeconds,
        newTransition: _contactNewTransition,
        dt,
      });
      const contactSource = wallContactSource(wallContact, {
        corridorHit,
        scrapeHit,
        obstacleHit,
        wallObstacleHit,
        meshHit,
        terrainHit,
      });
      collisionDebug.active = wallContact.hit;
      collisionDebug.source = contactSource;
      collisionDebug.impactSpeed = wallContact.hit ? wallContact.impactSpeed : 0;
      collisionDebug.slideSpeed = wallContact.hit ? wallSlideSpeed(wallContact, racer.state.velocity) : 0;
      collisionDebug.contactSeconds = wallContact.hit ? wallContactSeconds : 0;
      collisionDebug.normalX = wallContact.hit ? wallContact.nx : 0;
      collisionDebug.normalZ = wallContact.hit ? wallContact.nz : 0;
      if (
        onRacingSurface
        && (scrapeHit.hit || wallObstacleHit.hit || obstacleHit.hit || meshHit.hit || corridorHit.hit)
      ) {
        syncRacerGroundHeight(0);
      }
      const emitImpact = dominantHit.hit && dominantHit.impactSpeed > 6 && (!wallContact.hit || !contactWasSame);
      if (emitImpact) {
        followCam.shakeImpulse(Math.min(0.6, dominantHit.impactSpeed * 0.04));
        gameAudio.notifyCollision(dominantHit.impactSpeed);
        vibrateImpact(dominantHit.impactSpeed);
        racer.applyCollisionVisualImpulse(dominantHit, physics);
        // Magnitude curve: 0 at the bounce-speed floor, 1 at the configured
        // reference. Angle factor (head-on = 1, glancing = 0) gates hitstop
        // separately from FOV/spark — sliding into a wall shouldn't dilate
        // time, but should still spark and bump FOV a little.
        const impactRange = Math.max(1, physics.collisionHitstopSpeedRef - physics.collisionMinBounceSpeed);
        const impact01 = THREE.MathUtils.clamp(
          (dominantHit.impactSpeed - physics.collisionMinBounceSpeed) / impactRange,
          0,
          1,
        );
        if (impact01 > 0) {
          // Soft time dilation: hold at 0.35× scale for up to maxMs scaled
          // by impact magnitude × angle² (so glancing hits get nothing,
          // head-on gets full slow-mo), then ease back to 1.0× over 60 ms.
          const angle01 = THREE.MathUtils.clamp(contactState.impactAngle, 0, 1);
          const angleGate = Math.max(0, angle01 - 0.25);
          const dilationStrength = impact01 * angleGate * angleGate;
          if (dilationStrength > 0.05) {
            loop.applyTimeDilation(0.35, physics.collisionHitstopMaxMs * dilationStrength, 60);
          }
          followCam.fovPunch(physics.collisionFovPunchDeg * impact01);
          // Tighten the chase position spring so the camera catches up to
          // the post-impact velocity in ~150 ms instead of lagging visibly.
          followCam.tightenFollow(1 + impact01 * 4);
          // Camera dodge: flinch away from the wall normal (~0.5 m at full
          // impact). Reads as "the world shoved the camera" rather than the
          // camera failing to keep up.
          const dodgeMag = impact01 * 0.5;
          followCam.cameraDodge(-dominantHit.nx * dodgeMag, -dominantHit.nz * dodgeMag);
          const tangentX = -dominantHit.nz;
          const tangentZ = dominantHit.nx;
          const outwardNx = -dominantHit.nx;
          const outwardNz = -dominantHit.nz;
          scrapes.impactBurst(
            racer.state.position.x + outwardNx * 0.4,
            racer.state.position.y + 0.3,
            racer.state.position.z + outwardNz * 0.4,
            tangentX,
            tangentZ,
            outwardNx,
            outwardNz,
            impact01,
          );
        }
      }

      // Continuous spark trickle while still on the wall — the "we are
      // still scraping" cue. Uses the unified contact_intensity signal so
      // the trickle ramps up on a hard scrape and falls off as you peel
      // away. Tangent direction comes from rotating the wall normal 90° in
      // the XZ plane.
      if (contactState.intensity > 0.05) {
        const cTangentX = -contactState.outwardNz;
        const cTangentZ = contactState.outwardNx;
        scrapes.spawnContactTrickle(
          contactState.contactX,
          contactState.contactY,
          contactState.contactZ,
          cTangentX,
          cTangentZ,
          contactState.outwardNx,
          contactState.outwardNz,
          contactState.intensity,
          dt,
        );
      }

      // Continuous camera lean toward the wall while contact intensity is
      // active. Project the inward wall direction onto the racer's right
      // axis to get a signed lean amount; scale by intensity so glancing
      // contact gets a small lean and a hard scrape pins the camera over.
      if (contactState.intensity > 0.02) {
        const headingForRoll = racer.state.heading;
        const rightX = Math.cos(headingForRoll);
        const rightZ = -Math.sin(headingForRoll);
        // -outward = into wall; positive dot with right = wall is on right
        const wallRightDot = (-contactState.outwardNx) * rightX + (-contactState.outwardNz) * rightZ;
        const wallRollTarget = wallRightDot * 0.15 * contactState.intensity;
        followCam.setWallContactRoll(wallRollTarget);
      } else {
        followCam.setWallContactRoll(0);
      }

      // Track-progress for the safe-respawn point. Snapshot every 30 frames
      // (~0.5s) when the racer is well inside the corridor.
      const trackPos = locateTrackPosition(track, racer.state.position.x, racer.state.position.z);
      safePointFrameCounter += 1;
      if (
        respawnState === 'live'
        && onTrack
        && Math.abs(trackPos.signedLateral) < Math.min(trackPos.widthLeft, trackPos.widthRight) * 0.7
        && safePointFrameCounter >= 30
      ) {
        safePointFrameCounter = 0;
        lastSafeRespawn.x = racer.state.position.x;
        lastSafeRespawn.z = racer.state.position.z;
        lastSafeRespawn.heading = racer.state.heading;
      }
      // Kill conditions: corridor escape (safety net) or cliff fall.
      if (respawnState === 'live') {
        const lateralMagnitude = Math.abs(trackPos.signedLateral);
        const corridorEdge = trackPos.signedLateral > 0
          ? trackPos.widthLeft + track.wallBuffer
          : trackPos.widthRight + track.wallBuffer;
        const escapedCorridor = lateralMagnitude > corridorEdge + KILL_LATERAL_DISTANCE;
        const fellOffWorld = racer.state.position.y < spawnGroundY - KILL_Y_DEPTH;
        if (escapedCorridor || fellOffWorld) {
          triggerRespawn();
          return;
        }
      }

      // Rolling start: timer waits until the racer crosses the start gate
      // for the first time, so the wind-up isn't charged to lap one. The
      // check runs after collision/corridor correction so the ghost start
      // state matches the corrected live state.
      if (mode === 'racing' && !lapTimer.state.running) {
        const dx = racer.state.position.x - startGate.x;
        const dz = racer.state.position.z - startGate.z;
        const sign = Math.sign(dx * startGate.tx + dz * startGate.tz);
        if (prevStartGateSign < 0 && sign >= 0) {
          startedTimingThisStep = beginTiming();
        }
        prevStartGateSign = sign;
      }

      if (mode === 'racing') {
        wrongWay.update(
          racer.state.position.x,
          racer.state.position.z,
          racer.state.velocity.x,
          racer.state.velocity.z,
          dt,
        );
        hud.setWrongWay(wrongWay.isWrongWay);
        if (lapTimer.state.running) {
          const speed = Math.hypot(racer.state.velocity.x, racer.state.velocity.z);
          offTrackBudget.update(onRacingSurface, speed, dt);
          // Going the wrong way is a definitive cheat signal — invalidate
          // immediately rather than waiting for the off-track budget to drain.
          if (wrongWay.isWrongWay) offTrackBudget.invalidate();
        }
      }

      if (mode === 'racing' && lapTimer.state.running && !startedTimingThisStep) {
        liveLapFixedMs = (liveLapFixedMs ?? 0) + dt * 1000;
        advanceLiveLapProgress();
        ghostRecorder.recordStep(
          inputState.throttle,
          inputState.brake,
          inputState.steer,
          onRacingSurface,
          captureGhostStartState(),
        );
      }

      // The ghost is armed while the player rolls up, then starts at the same
      // timing-gate event as the lap clock.
      if (mode === 'racing') {
        if (opponentRaceEnabled) {
          ghostReplayActive = false;
          ghostShip.setVisible(false);
          latestGhostDeltaMs = null;
          hud.setGhostTracker(null);
        } else {
        const ghostShouldRun = ghostReplayActive
          && !startedTimingThisStep
          && activeGhost !== null
          && !ghostShip.isFinished();
        if (ghostShouldRun) ghostShip.step(dt);
        ghostShip.setVisible(ghostReplayActive && activeGhost !== null && !ghostShip.isFinished());
        if (activeGhost) {
          latestGhostDeltaMs = ghostReplayActive
            ? computeGhostProgressDelta(liveLapFixedMs, liveLapProgress, ghostProgressSamples)
            : null;
        } else {
          latestGhostDeltaMs = null;
          hud.setGhostTracker(null);
        }
        }
      }

      if (lapTimer.state.running) {
        const crossed = cpTracker.update(racer.state.position.x, racer.state.position.z);
        if (crossed >= 0) {
          const ev = lapTimer.notifyCheckpoint(crossed as 0 | 1 | 2, performance.now(), {
            updateBests:
              !devMode
              && !isAssistedMode(currentRaceMode)
              && !offTrackBudget.isLapInvalidated,
          });
          if (ev.sectorClosed) {
            hud.notifySectorClose(
              ev.sectorClosed.isPersonalBest,
              ev.sectorClosed.delta,
              ev.sectorClosed.sector,
            );
          }
          if (ev.lapClosed) {
            if (autoRaceActive) completeAutopilotLap(autopilotState, ev.lapClosed.total, raceSystem.stepCount);
            offTrackBudget.notifyLapBoundary();
            hud.notifyLapClose(ev.lapClosed.isPersonalBest, ev.lapClosed.total);
            const finalized = ghostRecorder.finalize(
              TRACK_ID,
              ev.lapClosed.total,
              PHYSICS_VERSION,
            );
            if (shouldSaveGhost({
              isPersonalBest: ev.lapClosed.isPersonalBest,
              hasGhost: personalGhost !== null,
              isAssisted: isAssistedMode(currentRaceMode),
            })) {
              if (finalized) {
                const wasUsingPersonalGhost = desiredGhost === personalGhost;
                setPersonalGhost(finalized);
                saveGhost(finalized, currentStorageScope());
                if (
                  wasUsingPersonalGhost
                  || desiredGhost === null
                  || desiredGhostLabel === 'Personal Best'
                  || desiredGhostLabel === 'Training Ghost'
                ) {
                  const matchingPersonalGhost = matchingLocalPersonalGhost();
                  setDesiredGhost(
                    matchingPersonalGhost,
                    matchingPersonalGhost ? 'Personal Best' : '',
                    matchingPersonalGhost ? 'Personal Ghost' : 'Target Ghost',
                  );
                }
              }
            }
            applyDesiredGhost();
            if (ev.lapClosed.isPersonalBest) {
              void applyTimingBoardRows(serverTimingRows);
            }
            ghostRecorder.markLapStart(captureGhostStartState());
            racer.state.driftCharge = 0;
            racer.state.boostTimer = 0;
            liveLapFixedMs = 0;
            resetLiveLapProgress();
            latestGhostDeltaMs = null;
            ghostShip.resetReplay();
            ghostReplayActive = !opponentRaceEnabled && activeGhost !== null;
            ghostShip.setVisible(ghostReplayActive);
            void handleLapClosed(
              ev.lapClosed.total,
              ev.lapClosed.sectors,
              finalized,
            );
          }
        }
      }

      const sp = racerSpeed(racer.state);
      perf.measure('particles', () => {
        if (perfToggles.particles.dust) {
          dust.spawn(racer.state.position, racer.state.heading, sp, !onRacingSurface, dt);
          dust.update(dt);
        } else {
          dust.clear();
        }
        racer.getEngineNozzleWorldPositions(engineNozzles);
        if (perfToggles.particles.engine) {
          // Per-engine exhaust: differential input drives each side's plume.
          // Nozzle order matches Racer.engineNozzleOffsets:
          // index 0 = world -X = pod's right engine, index 1 = world +X = left.
          const leftIntensity = inputState.leftThrust !== undefined
            ? Math.max(0, inputState.leftThrust)
            : inputState.throttle;
          const rightIntensity = inputState.rightThrust !== undefined
            ? Math.max(0, inputState.rightThrust)
            : inputState.throttle;
          engineIntensities[0] = rightIntensity;
          engineIntensities[1] = leftIntensity;
          if (racer.state.boostTimer > 0) {
            engineIntensities[0] = Math.max(engineIntensities[0], 1.6);
            engineIntensities[1] = Math.max(engineIntensities[1], 1.6);
          }
          engines.spawn(engineNozzles, racer.state.heading, sp, engineIntensities, dt);
          perf.measure('engines', () => engines.update(dt));
        } else {
          engines.clear();
        }
        racer.getScrapeEmitters(scrapeEmitters);
        if (perfToggles.particles.scrape) {
          scrapes.spawn(scrapeEmitters, racer.state.heading, sp, dt);
          perf.measure('scrapes', () => scrapes.update(dt));
        } else {
          scrapes.clear();
        }
        // Ground-streak speed band: ramp in over 30%–100% of maxSpeed so cruising
        // gets gentle streaks and full throttle gets a dense rush. Same speed01
        // basis as the radial blur drive in render(), but without the 70% cutoff
        // so streaks are visible earlier than the radial burst.
        const speedFactor = THREE.MathUtils.smoothstep(
          sp / Math.max(1, physics.maxSpeed),
          0.3,
          1.0,
        );
        if (perfToggles.particles.streaks) {
          groundStreaks.spawn(
            racer.state.position,
            racer.state.velocity,
            racer.state.position.y,
            speedFactor,
            dt,
          );
          groundStreaks.update(dt, terrain.sampleHeight);
        } else {
          groundStreaks.clear();
        }
      });

      // Drive the audio synth from physics each tick. Speed is normalized by
      // the active maxSpeed so the engine maps the same regardless of tuning.
      const speed01 = Math.max(0, Math.min(1, sp / Math.max(1, physics.maxSpeed)));
      // Same drift signal used by the boost meter: velocity/heading slip,
      // speed, and yaw commitment. Keeps Slip Rasp tied to real drift.
      const slip01 = driftAudioSignal(racer.state, physics);
      perf.measure('audio', () => {
        gameAudio.update({
          throttle: throttleSignal,
          brake: inputState.brake,
          speed: speed01,
          slip: slip01,
          load: Math.max(throttleSignal, slip01 * 0.5),
          onTrack: onRacingSurface,
          boost: racer.state.boostTimer > 0 ? 1 : 0,
          boostCharge: racer.state.driftCharge,
          airBrake: inputState.brake,
        }, dt);
      });

      // Continuous scrape — sum the active scrape emitters from the visual
      // layer; off-track surface adds a base level.
      let scrapeIntensity = 0;
      for (const e of scrapeEmitters) {
        if (e.intensity > scrapeIntensity) scrapeIntensity = e.intensity;
      }
      const offTrackScrape = !onRacingSurface ? speed01 * 0.45 : 0;
      // Drive wall scrape from the unified contact_intensity signal so the
      // audio rides smoothly across initial bonk, sustained grind, and fade
      // after leaving the wall. Baseline 0.2 even at low intensity so simply
      // touching the wall is audibly distinct from being free of it.
      const wallScrape = contactState.intensity > 0.05
        ? Math.min(1, 0.2 + contactState.intensity * 0.85)
        : 0;
      const finalScrape = Math.max(scrapeIntensity, offTrackScrape, wallScrape);
      const scrapeSurface = wallScrape >= Math.max(scrapeIntensity, offTrackScrape) ? 'wall' : onRacingSurface ? 'track' : 'dirt';
      const scrapeRoughness = scrapeSurface === 'wall' ? 0.82 : 0.55;
      if (finalScrape > 0.02) gameAudio.setScrape(finalScrape, speed01, scrapeRoughness, scrapeSurface);
      else gameAudio.stopScrape();
    },
    render: (_alpha, frameDt, clock) => {
      raceSystem.incRender();
      // Reset once per frame; renderer.info.autoReset is disabled so PerfHud
      // sees cumulative totals across the whole post-fx pipeline rather than
      // just the last pass. See src/render/Renderer.ts.
      renderer.info.reset();
      const isRacingFrame = mode === 'racing';
      const visualFrameDt = isRacingFrame ? clampVisualSmoothingDt(frameDt) : frameDt;
      const shadowsEnabled = perfToggles.shadowRig && shadowConfig.enabled;
      renderer.shadowMap.enabled = shadowsEnabled;
      if (isRacingFrame && adaptiveRenderQuality && !reducedRenderLoad && adaptiveQualityGate.tick(frameDt)) {
        reduceRenderLoad(`${(frameDt * 1000).toFixed(0)}ms frame`);
      }
      // Dynamic resolution scaling: drive off wall-clock frame time. With
      // vsync, frame time settles at the monitor's target (e.g. 16.67ms on
      // 60Hz) when the GPU is keeping up, and creeps above when it's not —
      // that's the cleanest, lowest-noise signal we can get in-browser.
      // GPU time queries are tempting but EXT_disjoint_timer_query inflates
      // measurements ~30% due to per-query GPU sync, which produces
      // false-positive "slow" readings and stair-steps DRS down even when
      // the game is comfortably at vsync. (We still display GpuTimer in the
      // HUD for diagnostic info — just don't trust it for control loops.)
      // Disabled while AdaptiveQuality has reduced render load to avoid
      // them fighting.
      if (isRacingFrame && drsSystem.mode === 'auto' && drs && !reducedRenderLoad) {
        if (drsSystem.tickRefreshProbe(performance.now()) !== null) applyDrsTargetBudget();
        const tickResult = drsSystem.tick(frameDt);
        if (tickResult.newScale !== null) applyRenderScale(tickResult.newScale);
        if (tickResult.floorStuckSevere && autoFrameCap && activeRenderHzCap > THERMAL_RENDER_FPS_CAP) {
          setActiveRenderHzCap(
            THERMAL_RENDER_FPS_CAP,
            `DRS floor (${tickResult.drsCurrent.toFixed(2)}) still over ${tickResult.slowMs.toFixed(1)}ms`,
          );
        }
        if (tickResult.floorStuckReduce && adaptiveRenderQuality) {
          reduceRenderLoad(`DRS floor (${tickResult.drsCurrent.toFixed(2)}) for ${DrsSystem.FLOOR_REDUCE_FRAMES} frames`);
        }
      }
      if (isRacingFrame) {
        perf.measure('racer.render', () => racer.render(_alpha, visualFrameDt, clock.renderTime));
        if (opponentRaceEnabled && opponentController && opponentRacer) {
          opponentRenderFrames++;
          opponentRacer.render(_alpha, visualFrameDt, clock.renderTime);
        }
        contactShadow.update(
          racer.state.position.x,
          racer.state.position.y,
          racer.state.position.z,
          latestGroundY + (perfToggles.kerbY ? latestKerbY : 0),
        );
        if (!opponentRaceEnabled) {
          perf.measure('ghost.render', () => ghostShip.render(_alpha, visualFrameDt, clock.renderTime));
        }
        perf.measure('dayNight', () => {
          if (perfToggles.dayNight && styleConfig.dayCycle.enabled && styleConfig.dayCycle.playing && styleConfig.dayCycle.durationSec > 0) {
            styleConfig.dayCycle.scrub = (styleConfig.dayCycle.scrub + visualFrameDt / styleConfig.dayCycle.durationSec) % 1;
            resolvedStyle = resolveStyle(styleConfig);
            applyResolvedSceneStyle();
          }
        });
        perf.measure('sunRig', () => {
          // Sun-rig targets must update every frame regardless of shadows. The
          // MAP view (src/debug/TrackReview.ts:869-876) runs these unconditionally
          // and that's what keeps the per-light direction tracking the focus
          // point as the racer moves. Skipping this when shadows are off used to
          // freeze the rim/fill/bounce light targets at boot.
          updateSunRig(sun, sunTarget, racer.group.position, keyLightDirectionToTarget(resolvedStyle, keyLightDir), shadowConfig.forwardBias);
          updateSunRig(fillSun, fillSunTarget, racer.group.position, fillLightDirectionToTarget(resolvedStyle, fillLightDir));
          updateSunRig(bounceLight, bounceTarget, racer.group.position, directionFromAnglesToTarget(
            resolvedStyle.sunRig.bounce.elevationDeg,
            resolvedStyle.sunRig.bounce.azimuthDeg,
            100,
            bounceLightDir,
          ));
          updateSunRig(rimLight, rimTarget, racer.group.position, rimLightDirectionToTarget(resolvedStyle, camera, rimLightDir));
          if (shadowsEnabled && throttleShadowMaps && webglRenderer) {
            shadowMapRefreshFrames++;
            shadowMapFocus.copy(sunTarget.position);
            const firstShadowFrame = !Number.isFinite(lastShadowMapFocus.x);
            const movedEnough = firstShadowFrame
              || shadowMapFocus.distanceToSquared(lastShadowMapFocus) >= SHADOW_MAP_REFRESH_DISTANCE_SQ;
            if (movedEnough || shadowMapRefreshFrames >= SHADOW_MAP_REFRESH_MAX_FRAMES) {
              webglRenderer.shadowMap.needsUpdate = true;
              lastShadowMapFocus.copy(shadowMapFocus);
              shadowMapRefreshFrames = 0;
            }
          } else if (!shadowsEnabled) {
            lastShadowMapFocus.set(Number.NaN, Number.NaN, Number.NaN);
            shadowMapRefreshFrames = SHADOW_MAP_REFRESH_MAX_FRAMES;
          }
        });
        perf.measure('camera', () => {
          // While the player is at the title screen the camera slowly orbits
          // the parked racer instead of running the chase update. That gives
          // the title a live cinematic backdrop AND naturally exercises every
          // material/shader at title time so the green flag doesn't stutter.
          if (mode === 'title') {
            followCam.updateCinematic(racer.group.position, visualFrameDt);
          } else if (perfToggles.cameraSmooth) {
            followCam.update(racer.group.position, racer.state.velocity, racer.group.rotation.y, visualFrameDt, racer.state.yawVelocity);
          } else {
            followCam.update(racer.group.position, racer.state.velocity, racer.group.rotation.y, 1, racer.state.yawVelocity);
          }
        });
        perf.measure('sky', () => {
          sky.follow(camera.position);
          // The volumetric cloud dome is positioned at the camera each
          // frame so the slab follows the player around the canyon.
          // Without this the dome sits at the world origin and the camera
          // can drive out of it.
          if (volumetricClouds) volumetricClouds.mesh.position.copy(camera.position);
        });
        perf.measure('scenery', () => {
          trackFurniture.update(visualFrameDt);
          cloudField.update(visualFrameDt);
        });
        perf.state({
          dtMs: frameDt * 1000,
          visualDtMs: visualFrameDt * 1000,
          pos: {
            x: racer.state.position.x,
            y: racer.state.position.y,
            z: racer.state.position.z,
          },
          cameraPos: {
            x: camera.position.x,
            y: camera.position.y,
            z: camera.position.z,
          },
          groundY: latestGroundY,
          kerbY: latestKerbY,
        });
        perf.measure('ghostHud', () => {
          syncGhostHud();
        });
        perf.measure('hud.livePosition', () => {
          hud.setLivePosition({
            lapMs: liveLapFixedMs,
            progress: liveLapProgress,
            totalLength: track.totalLength,
            pbSamples: pbProgressSamples,
            comparisonSamples: ghostProgressSamples,
          });
        });
        perf.measure('hud.update', () => {
          hud.update(performance.now(), racerSpeed(racer.state));
          hud.setDrift(racer.state.driftCharge, racer.state.boostTimer > 0);
        });
        perf.measure('minimap.update', () => {
          minimap.update(
            racer.group.position.x,
            racer.group.position.z,
            racer.group.rotation.y,
            lapTimer.state.currentSectorIndex,
          );
        });
        // Radial blur drive uses the pure `computeMotionBlurStrength` from
        // MotionBlurPrefs.ts — pinned by tests in src/__tests__/MotionBlur.test.ts.
        // Speed band starts at 40% of maxSpeed so the effect is visible across
        // the typical race speed range, ramping to full at maxSpeed. A future
        // boost mechanic can stack on top by lowering the band low or boosting
        // intensity transiently.
        if (bloom && perfToggles.radialBlur && !reducedRenderLoad) {
          perf.measure('postfx.radialBlurControl', () => {
            postFx.updateMotionBlur({
              speed: racerSpeed(racer.state),
              maxSpeed: physics.maxSpeed,
              resolvedStyle,
              visualFrameDt,
              camera,
            });
          });
        } else {
          postFx.zeroMotionBlur();
        }
      } else {
        postFx.zeroMotionBlur();
      }
      // God-ray sun projection. WebGL stubs this out, and WebGPU keeps the
      // accumulator opt-in via ?godRays=on so the default backend comparison
      // does not pay for an extra full-screen 48-tap effect.
      if (bloom) {
        const sunDir = SHARED_AERIAL_UNIFORMS.uSunDirWorld.value;
        // Anchor a far point in the sun direction relative to the camera so
        // the projection encodes "look toward the sun" regardless of where
        // the camera is in the world.
        godRaySunWorld
          .copy(sunDir)
          .multiplyScalar(1e5)
          .add(camera.position);
        godRaySunNDC.copy(godRaySunWorld).project(camera);
        // NDC.z in (-1, 1) means in front of the camera. Outside that range
        // (including > 1 for behind), kill the strength so we don't scatter
        // a phantom shaft from a back-projected sun.
        const inFront = godRaySunNDC.z >= -1 && godRaySunNDC.z <= 1;
        // Soft fade as the sun edges off-screen so the shafts don't pop.
        const offscreen = Math.max(
          0,
          Math.max(Math.abs(godRaySunNDC.x), Math.abs(godRaySunNDC.y)) - 1.0,
        );
        const edgeFade = Math.max(0, 1 - offscreen / 0.6);
        // 0.55 puts the shafts visible-but-subtle in the sky region without
        // bleeding the warm sun tint across mid-ground geometry. Earlier
        // 1.6 washed the asphalt pink and erased the exhaust streaks; the
        // accumulator is additive across all pixels, so even the road
        // picked up the radial bias and lost contrast.
        const baseStrength = 0.55;
        const strength = inFront ? baseStrength * edgeFade : 0;
        bloom.setGodRaySun(godRaySunNDC.x, godRaySunNDC.y, strength);
        if (isRacingFrame && perfToggles.heat && !reducedRenderLoad) {
          bloom.setHeatPoints(racer.getPlumeHeatPoints(plumeHeatPoints));
          bloom.setHeatIntensity(racer.getPlumeIntensity());
        } else {
          bloom.setHeatIntensity(0);
        }
      }
      const debugBits = gameDebugLayerBits();
      if (debugLayersDirty || debugBits !== lastGameDebugLayerBits) {
        perf.measure('debugLayers.apply', () => {
          applyDebugLayers(gameDebugLayers, resolvedStyle, {
            renderer,
            scene,
            skyMesh: sky.mesh,
            keyLight: sun,
            fillLight: fillSun,
            hemisphere,
            bounceLight,
            rimLight,
            fog: worldFog,
            emissiveGroups: [racer.group],
            bloom,
          });
        });
        lastGameDebugLayerBits = debugBits;
        debugLayersDirty = false;
      }
      const webgpuPixelationActive = rendererHandle.isWebGPU
        && pixelation.config.enabled
        && perfToggles.pixel
        && bloom !== null;
      const bloomEffectActive = bloomEnabled && perfToggles.bloom;
      bloom?.setBloomEnabled(bloomEffectActive);
      postFx.syncPixelation(webgpuPixelationActive);
      if ((!bloomEffectActive && !webgpuPixelationActive) || !bloom) {
        perf.measure('renderer.render', () => {
          withGpuTimer('gpu.scene', () => {
            renderer.setRenderTarget(null);
            renderer.render(scene, camera);
          });
        });
      } else if (pixelation.config.enabled && webglRenderer) {
        if (perfToggles.pixel) {
          perf.measure('postfx.pixel', () => {
            withGpuTimer('gpu.pixel', () => pixelation.render(webglRenderer, scene, camera));
          });
        } else {
          perf.measure('renderer.render', () => {
            withGpuTimer('gpu.scene', () => {
              renderer.setRenderTarget(null);
              renderer.render(scene, camera);
            });
          });
        }
      } else {
        perf.measure('postfx.bloom', () => {
          const traceHooks = postfxPassTrace
            ? {
              measureCpu: (name: string, fn: () => void) => perf.measure(name, fn),
              measureGpu: gpuPostfxPassTrace
                ? (name: string, fn: () => void) => withGpuTimer(name, fn)
                : undefined,
            }
            : undefined;
          if (gpuPostfxPassTrace) bloom.render(visualFrameDt, traceHooks);
          else withGpuTimer('gpu.bloom', () => bloom.render(visualFrameDt, traceHooks));
        });
      }
      gpuTimer?.tickCompute();
      gpuTimer?.tick();
      perfHud?.tick();
      perfDiagPanel?.tick();
      perfLabPanel?.tick();
    },
  });

  // Wire transitions now that loop and all screens exist.
  t.toTitle = () => {
    mode = 'title';
    loop.paused = true;
    resetRunState();
    opponentRacer.group.visible = false;
    uiSystem.enterTitle();
    document.body.classList.remove('is-racing');
    drsSystem.floorSlowFrames = 0;
    drsSystem.floorSmoothedMs = 0;
    lastShadowMapFocus.set(Number.NaN, Number.NaN, Number.NaN);
    shadowMapRefreshFrames = SHADOW_MAP_REFRESH_MAX_FRAMES;
    inputSystem.resetTouch();
    titleThemeMusic.play();
    raceMusic.stop();
    gameAudio.silenceEngine();
    void gameAudio.suspend();
    void wakeLock.release();
  };
  t.toRacing = () => {
    resetRunState();
    startRaceCountdown();
    mode = 'racing';
    loop.paused = false;
    opponentRacer.group.visible = opponentRaceEnabled;
    loadRacerVisual();
    titleThemeMusic.fadeOut();
    uiSystem.enterRace(followCam.mode);
    syncCameraHudMode();
    document.body.classList.add('is-racing');
    drsSystem.floorSlowFrames = 0;
    drsSystem.floorSmoothedMs = 0;
    lastShadowMapFocus.set(Number.NaN, Number.NaN, Number.NaN);
    shadowMapRefreshFrames = SHADOW_MAP_REFRESH_MAX_FRAMES;
    cpTracker.reset();
    void wakeLock.acquire();
    if (touchActive) tryLockLandscape();

    // Audio: start the context (idempotent) on the first race entry — the
    // user's click on Race counts as the autoplay-policy gesture. After
    // start resolves, fire ignition. Subsequent visits skip straight to
    // ignite since start() short-circuits.
    void gameAudio.start().then(() => gameAudio.resume()).then(() => {
      gameAudio.playIgnition();
    }).catch(() => { /* already logged inside start() */ });

    // Race music swells in after the title fade (~1200ms) and the ignition
    // SFX (~600ms) have had clean air. Result: title → silence-with-ignition
    // → race music — the engine start acts as the bridge between songs.
    window.setTimeout(() => {
      if (mode === 'racing') raceMusic.fadeIn();
    }, 1200);
  };
  t.toPaused = () => {
    if (mode !== 'racing') return;
    mode = 'paused';
    loop.paused = true;
    uiSystem.enterPause();
    // Hard-stop race music so its audio element is fully torn down before
    // title music starts. pause() alone could leave the element resumable by
    // an in-flight gesture-retry / 'ended' handler, causing audible overlap.
    raceMusic.stop();
    inputSystem.resetTouch();
    gameAudio.silenceEngine();
    void gameAudio.suspend();
    titleThemeMusic.play();
  };
  t.fromPaused = () => {
    if (mode !== 'paused') return;
    mode = 'racing';
    loop.paused = false;
    uiSystem.exitPause();
    drsSystem.floorSlowFrames = 0;
    drsSystem.floorSmoothedMs = 0;
    lastShadowMapFocus.set(Number.NaN, Number.NaN, Number.NaN);
    shadowMapRefreshFrames = SHADOW_MAP_REFRESH_MAX_FRAMES;
    titleThemeMusic.fadeOut();
    void gameAudio.resume();
    // Fresh track + smooth fade-in (matches the title→race entry).
    raceMusic.fadeIn();
  };
  t.resetLap = () => {
    resetRunState();
    if (mode === 'racing') startRaceCountdown();
  };

  let scenePanel: ScenePanel | null = null;
  if (devMode) {
    scenePanel = new ScenePanel({
      mode: runtime.scenePanelMode,
      assembly: sceneAssembly,
      // Full StylePanel mount: this is what carries the cloud / kerb / edge
      // line / ground / sky / oil sheen / rubber / crack folders that
      // disappeared from the GAME dock when ScenePanel only forwarded the
      // tuning surface. StylePanel writes through the same
      // dispatchStyleConfigChange path as the TuningPanel folders below, so
      // both panels edit the same live styleConfig and stay in sync.
      style: {
        config: styleConfig,
        preset: activePreset.isFallback
          ? undefined
          : {
              name: activePreset.name,
              label: activePreset.label,
              isFallback: activePreset.isFallback,
            },
        shadows: {
          sun,
          shadowConfig,
          applyShadowConfig,
          contactShadow,
        },
        debugLayers: gameDebugLayers,
        onSaveStatus: (message, ok) => {
          if (ok) devDock?.flashSaved(message);
          else devDock?.setStatus(message, 'error');
        },
        onChange: applyRuntimeStyle,
      },
      tuning: {
        physics,
        camera: followCam,
        input,
        racer,
        pixelation,
        renderScale: renderScaleTuning,
        collisionDebug,
        engineTrail: engines,
        dustTrail: dust,
        rendererMode: rendererHandle.mode,
        particlesLayer: {
          file: `src/scene/templates/particles/${sceneAssembly.layers.particles.id}.json`,
          layer: sceneAssembly.layers.particles,
        },
        style: styleConfig,
        onStyleChange: applyRuntimeStyle,
        sun,
        shadowConfig,
        applyShadowConfig,
        contactShadow,
        preset: {
          trackUrl: TRACK_URL,
          activeName: activePreset.name,
          isFallback: activePreset.isFallback,
        },
      },
    });
    if (devDock) devDock.setContent(scenePanel.root);
    else document.body.appendChild(scenePanel.root);
  }
  bootMark('ui + tuning ready');

  // Prefetch the pod-racer GLB during the title screen so the racer's
  // materials are present in the scene by the time we warm shaders.
  // Without this, loadRacerVisual fires on race entry, the racer's
  // unique materials compile JIT on the first racing frame, and the
  // user sees the racer stutter while the background is fine (the
  // background materials compiled when the title scene first rendered).
  // ghostShip materials share the same GLB so loading both up front
  // also warms the ghost-proxy shaders for replay races.
  bootPhase = 'racer';
  updateTitleStatus();
  const racerVisualReady = loadRacerVisual();

  loop.start();
  loop.paused = true; // Hold at title until the player clicks Race.

  // Shader warmup: AssetSystem.warmupShadersAfter() compiles every
  // material/shader variant in the scene before the first racing frame so
  // the racer doesn't hitch at the green flag while WebGL first encounters
  // each material. Waits for the racer GLB first — the assembly loader
  // rebuilds the racer.group hierarchy and its custom-shader materials,
  // so warming before that load completes would miss exactly the
  // materials that cause the green-flag stutter.
  const warmupOrTimeout = assetSystem.warmupShadersAfter(
    racerVisualReady,
    renderer,
    scene,
    camera,
    {
      onWarmupStart: () => { bootPhase = 'warmup'; updateTitleStatus(); },
      onShadersCompiled: (compileMs) => {
        bootMark('shaders compiled');
        perf.event('compileAsync.boot', { ms: compileMs });
      },
      registerWebGPUCompatibility,
    },
  );
  // The Vibe Jam badge crowds the racing controls on phones — its
  // bottom-right anchor lands right on top of the BOOST button — so
  // skip it entirely when we know we're on a touch device.
  if (!touchActive) deferVibeJamWidget();
  registerServiceWorker();
  applyLefty(leftyLayout.isEnabled());

  // Debug hooks for end-to-end tests. Each system that owns refs
  // (Input/Track/Race/UI/Render/PostFX) contributes them via
  // debugEntries(); bootstrap registers those into the DebugRegistry.
  // Remaining fields below are not yet owned by a system (boot-tracking,
  // sun rig, particles, GameLoop, scene-style panel, perfLab plumbing,
  // and the cross-cutting diagnostics() aggregator).
  for (const sys of [inputSystem, trackSystem, raceSystem, uiSystem, renderSystem, postFx]) {
    for (const [key, value] of Object.entries(sys.debugEntries())) {
      diagnostics.registry.registerStatic(key, value);
    }
  }
  diagnostics.registry.registerStatic('getRaceMode', () => currentRaceMode);
  diagnostics.registry.registerStatic('setRaceMode', setRaceMode);
  diagnostics.install({
    followCam,
    engines,
    scrapes,
    loop,
    physicsStep,
    ghostDebug: () => ({
      activeKind: isTrainingGhostRecording(activeGhost) ? 'training' : activeGhost ? 'recording' : 'none',
      desiredKind: isTrainingGhostRecording(desiredGhost) ? 'training' : desiredGhost ? 'recording' : 'none',
      activeLabel: activeGhostLabel,
      desiredLabel: desiredGhostLabel,
      activeTitle: activeGhostTitle,
      desiredTitle: desiredGhostTitle,
      activeRaceTarget,
      desiredRaceTarget,
      replayActive: ghostReplayActive,
      visible: ghostShip.group.visible,
      finished: ghostShip.isFinished(),
    }),
    syncRacerGroundHeight,
    enterRacing: () => t.toRacing(),
    enterTitle: () => t.toTitle(),
    get autopilotLaps() { return getAutopilotLaps(autopilotState); },
    autopilotDebug: () => ({
      active: autoRaceActive,
      preset: autopilotPreset ?? 'default',
      mode: autopilotState.mode,
      laps: autopilotState.telemetry.laps.length,
      frames: autopilotState.telemetry.frames.length,
      lastLapMs: autopilotState.telemetry.laps.at(-1)?.lapTimeMs ?? null,
      lastSafetyOverride: autopilotState.lastSafetyOverride,
      lastFrame: autopilotState.telemetry.frames.at(-1) ?? null,
    }),
    get opponents() { return opponentRuntimeDiagnostics(); },
    get bootMarks() { return bootMarks.slice(); },
    warmupMs: AssetSystem.WARMUP_TIMEOUT_MS,
    camera,
    THREE,
    sky,
    sun,
    fillSun,
    bounceLight,
    rimLight,
    scenePanel,
    tuningPanel: scenePanel?.tuningPanel,
    listSliders: () => scenePanel?.listSliders() ?? [],
    perfSnapshot: () => perf.snapshot(),
    gpuStats: () => gpuTimer?.stats() ?? null,
    sceneAudit: () => collectSceneAudit(scene, renderer),
    perfLab: perfLab
      ? {
        snapshot: (options?: { includeScene?: boolean }) => perfLab.snapshot(options),
        trace: (options?: { durationMs?: number; sampleIntervalMs?: number; includeScene?: boolean }) => perfLab.trace(options),
        show: () => perfLabPanel?.show(),
      }
      : null,
    setRenderHzCap: (hz: number) => {
      setDiagRenderHzOverride(hz, 'diag');
    },
    getLoopRenderHzCap: () => loop.getMaxRenderHz(),
    diagnostics: () => ({
      mode,
      reducedRenderLoad,
      reducedRenderLoadAt,
      reducedRenderLoadReason,
      terrainShaderMode,
      asphaltShaderMode,
      asphaltLiveDetail,
      stylePreset: activePreset.name || null,
      stylePresetSource: useStyleOverlay ? 'dev-overlay' : 'saved-preset',
      stylePresetHasOverlay: activePreset.hasOverlay,
      styleTrackUrl: activePreset.trackUrl,
      crackDecals: bakedCracks ? 'baked' : 'procedural',
      bakedCracksRequested: bakedCracksEnabled,
      surfaceAssets: bakedSurfaceAssets ? 'bakedPbr' : 'procedural',
      bakedSurfacesRequested: bakedSurfacesEnabled,
      volumetricClouds: volumetricClouds ? 'webgpu-raymarch' : 'off',
      staticBundles: staticBundlesEnabled && rendererHandle.webgpuBundleGroupCtor ? 'webgpu-bundlegroup' : 'plain-group',
      staticBundleKerbs: bundleKerbsEnabled,
      staticBundleFurniture: bundleFurnitureEnabled,
      drsApplyMode,
      drsRequestedMode,
      renderedFrameCount: loop.renderedFrameCount,
      renderSkipCount: loop.renderSkipCount,
      stepCount: raceSystem.stepCount,
      autopilot: {
        active: autoRaceActive,
        preset: autopilotPreset ?? 'default',
        mode: autopilotState.mode,
        laps: autopilotState.telemetry.laps.length,
        frames: autopilotState.telemetry.frames.length,
        lastLapMs: autopilotState.telemetry.laps.at(-1)?.lapTimeMs ?? null,
        lastSafetyOverride: autopilotState.lastSafetyOverride,
        lastFrame: autopilotState.telemetry.frames.at(-1) ?? null,
      },
      opponents: opponentRuntimeDiagnostics(),
      opponentRuntime: {
        active: opponentRaceEnabled,
        hasController: opponentController !== null,
        hasRacer: opponentRacer !== null,
        renderFrames: opponentRenderFrames,
        wrappedRenderFrames: opponentWrappedRenderFrames,
        resetCount: opponentResetCount,
        lastError: opponentLastError,
      },
      renderHzCap: Number.isFinite(diagRenderHzOverride ?? activeRenderHzCap) ? (diagRenderHzOverride ?? activeRenderHzCap) : 'uncapped',
      requestedRenderHzCap: Number.isFinite(requestedRenderHzCap) ? requestedRenderHzCap : 'uncapped',
      autoFrameCap,
      detectedRefreshHz: drsSystem.detectedRefreshMs ? 1000 / drsSystem.detectedRefreshMs : null,
      ...drsSystem.snapshot(),
      renderer: {
        mode: rendererHandle.mode,
        requestedMode: rendererHandle.requestedMode,
        fallbackReason: rendererHandle.fallbackReason,
        pixelRatio: renderer.getPixelRatio(),
        samples: (renderer as { samples?: number }).samples ?? null,
        shadowMapEnabled: renderer.shadowMap.enabled,
        shadowMapAutoUpdate: webglRenderer?.shadowMap.autoUpdate ?? null,
        shadowMapNeedsUpdate: webglRenderer?.shadowMap.needsUpdate ?? null,
        postProcessing: rendererHandle.capabilities.postProcessing,
        webgpuTimestamps: rendererHandle.capabilities.webgpuTimestamps,
      },
      webgpuCompatibility: renderSystem.webgpuCompatibilityReport,
      perf: perf.snapshot(),
      gpu: gpuTimer?.stats() ?? null,
      scene: collectSceneAudit(scene, renderer),
    }),
  });
  type GameLoopUpdate = (dt: number, clock: GameLoopStepClock) => void;
  const loopUpdateTarget = loop as unknown as { opts: { update: GameLoopUpdate } };
  const origUpdate = loopUpdateTarget.opts.update;
  loopUpdateTarget.opts.update = (dt: number, clock: GameLoopStepClock) => {
    raceSystem.incStep();
    origUpdate(dt, clock);
  };

  // Hold the Race button until the racer GLB is loaded AND
  // compileAsync has either finished or timed out. Without this, on
  // Safari/Firefox (no KHR_parallel_shader_compile) the user could
  // click Race during the sync compile and re-trigger the green-flag
  // stutter. The 3s timeout ensures we never lock the title screen
  // indefinitely if the warmup hangs — accept a possible micro-hitch
  // over a possible hang. Mirrors UE5 PSO precaching's timeout pattern.
  await warmupOrTimeout;

  bootPhase = 'ready';
  bootReady = true;
  title.setRaceEnabled(true);
  // Reveal the cinematic backdrop now that the loop is running and every
  // material has been warmed. Crossfading here (rather than on first
  // paint) hides the black-canvas-during-boot phase.
  title.setCinematic(true);
  updateTitleStatus();
  bootMark('boot ready');
  if (params.has('autoRace') || opponentRaceRequested) t.toRacing();
  const diagMode = params.get('diag');
  if ((diagMode === 'full' || diagMode === 'stress') && perfLab) {
    void launchDiagnosticRun(perfLab, {
      trackId: TRACK_ID,
      landscape: landscapeVersion,
      raceMode: String(currentRaceMode),
      diagMode,
    });
  }
}
